import axios from 'axios';
import { API_BASE_URL } from '../../config/env';
import type {
  AdminCompanyMatchingRefreshResponse,
  AdminCompanyPayoutReview,
  AdminBusinessProfile,
  AdminAssistedDispatchQueueItem,
  AdminAssistedDispatchCandidate,
  AdminAssistedDispatchAssignResponse,
  AdminCommissionSummary,
  AdminCustomerPromotion,
  AdminCustomerPromotionPayload,
  AdminDeclaredLoadDashboard,
  AdminDeclaredLoadDashboardParams,
  AdminDeclaredLoadListItem,
  AdminDeclaredLoadListParams,
  AdminDriverBankDetailsPayload,
  AdminDriverCampaign,
  AdminDriverCampaignDelivery,
  AdminDriverCampaignEnrollment,
  AdminDriverCampaignEnrollmentBatch,
  AdminDriverCampaignEnrollmentPayload,
  AdminDriverCampaignEnrollmentRemovalPayload,
  AdminDriverCampaignLedgerTransaction,
  AdminDriverCampaignPaymentPayload,
  AdminDriverCampaignPayload,
  AdminDriverCampaignSummary,
  AdminDriverCompanyDetailsPayload,
  AdminDriverEarningRecord,
  AdminDriverFileValidityPayload,
  AdminEngagementDashboard,
  AdminEngagementDashboardParams,
  AdminFaceLivenessReview,
  AdminDriverGitReviewPayload,
  AdminDriverListItem,
  AdminDriverOnboardingStartPayload,
  AdminDriverOperatingTownPayload,
  AdminDriverReviewDetail,
  AdminDriverTownCoverage,
  AdminDriverVehicleDetailsPayload,
  AdminEmailCampaign,
  AdminEmailCampaignDetail,
  AdminEmailCampaignPayload,
  AdminEmailCampaignPreview,
  AdminEmailCampaignRecipient,
  AdminEmailRecipientPreview,
  AdminQueueSummary,
  AdminReviewConversation,
  AdminReviewMessage,
  AdminSupportInboxPage,
  AdminTripDetail,
  AdminTripListItem,
  AdminTripResolutionPayload,
  AdminTripResolutionResponse,
  AdminTripsSummary,
  AdminWhatsAppMessageResponse,
  AdminUser,
  PaystackSubaccountUpdatePayload,
} from '../../types/admin';
import type {
  AdminPaymentActionPayload,
  AdminPaymentIntent,
  AdminPaymentIntentDetail,
  AdminPaymentIntentSearchParams,
  AdminManualCollection,
  AdminManualCollectionPayload,
  AdminManualCollectionSearchParams,
  AdminPayout,
  AdminPayoutBatchRun,
  AdminPayoutSearchParams,
  AdminProviderEvent,
  AdminProviderEventSearchParams,
  AdminWebhookEvent,
  AdminWebhookEventSearchParams,
  AdminLedgerEntry,
  AdminPaymentActionAudit,
} from '../../types/adminPaymentOps';
import { sanitizeApiErrorMessage } from './errorMessages';
import { clearAdminAuthFlow } from '../storage/adminFlow';
import { getWebSessionContext } from '../storage/authTokens';
import { attachRoleAuthInterceptors } from './authInterceptors';
import { logoutRoleSession, normalizeTokenBundle, refreshRoleSession } from './tokenRefresh';

const api = axios.create({
  baseURL: API_BASE_URL,
});

const publicApi = axios.create({
  baseURL: API_BASE_URL,
});

const ADMIN_PAYMENT_OPS_BASE = '/payment-service/admin/payment-ops';
const ADMIN_COMMUNICATIONS_BASE = '/notification-service/api/admin/communications';

attachRoleAuthInterceptors(api, {
  role: 'admin',
  clearSession: clearAdminAuthFlow,
});

function normalizeAdminApiErrorMessage(rawMessage: string, fallbackMessage: string): string {
  const message = rawMessage.trim();
  if (!message) return fallbackMessage;

  if (/admin role is required/i.test(message)) {
    return 'This page is only available to admin accounts.';
  }

  if (/super admin role is required/i.test(message)) {
    return 'This action is only available to super admin accounts.';
  }

  if (/idempotency key has already been recorded|idempotency key.*already/i.test(message)) {
    return 'This action was already submitted with the same idempotency key. Refresh the page and try again only if the action did not complete.';
  }

  if (/reason is required|reason.*required/i.test(message)) {
    return 'Please enter an operational reason before submitting this admin action.';
  }

  if (/over.?collect|exceed.*agreed|collected.*exceed/i.test(message)) {
    return 'This collection would exceed the agreed amount. Check the amount or update the agreed amount before submitting.';
  }

  if (/refund|dispute|chargeback/i.test(message) && /manual collection|offline collection|cannot|reject/i.test(message)) {
    return 'Manual collection cannot be declared while refund, dispute, or chargeback handling is active on this payment.';
  }

  if (/amount must be greater|amount.*positive|amount.*0\.01/i.test(message)) {
    return 'Enter a collection amount greater than zero.';
  }

  if (/webhook has already been processed|already processed/i.test(message)) {
    return 'This webhook has already been processed, so it cannot be replayed.';
  }

  if (/invalid signature|cannot replay.*invalid signature/i.test(message)) {
    return 'This webhook has an invalid signature and cannot be replayed safely.';
  }

  if (/payout state .* cannot be manually held/i.test(message)) {
    const state = message.match(/payout state\s+([A-Z_]+)/i)?.[1];
    return state
      ? `This payout is ${state.replaceAll('_', ' ')} and cannot be placed on hold.`
      : 'This payout is in a state that cannot be placed on hold.';
  }

  if (/only ON_HOLD payouts can be released/i.test(message)) {
    return 'Only payouts that are currently on hold can be released.';
  }

  if (/payment intent is not captured or settled/i.test(message)) {
    return 'This payout cannot be released because the payment is not captured or settled yet.';
  }

  if (/payout cannot be released while refund activity exists/i.test(message)) {
    return 'This payout cannot be released while refund activity exists on the payment.';
  }

  return sanitizeApiErrorMessage(message, fallbackMessage);
}

function extractErrorMessage(error: unknown, fallbackMessage: string): string {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;

    if (typeof responseData === 'string' && responseData.trim()) {
      return normalizeAdminApiErrorMessage(responseData, fallbackMessage);
    }

    if (responseData && typeof responseData === 'object') {
      const data = responseData as Record<string, unknown>;
      const candidate =
        (typeof data.message === 'string' && data.message) ||
        (typeof data.error === 'string' && data.error) ||
        (typeof data.details === 'string' && data.details) ||
        (typeof data.detail === 'string' && data.detail) ||
        (typeof data.title === 'string' && data.title);

      if (candidate) {
        return normalizeAdminApiErrorMessage(candidate, fallbackMessage);
      }
    }

    if (error.response?.status === 403) {
      return 'This page is only available to admin accounts.';
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return normalizeAdminApiErrorMessage(error.message, fallbackMessage);
  }

  return fallbackMessage;
}

/**
 * Generic over the params object rather than taking `Record<string, unknown>`.
 *
 * The search-param types are interfaces, and an interface has no implicit index
 * signature — TypeScript rejects it as a `Record<string, unknown>` because a
 * later declaration merge could add an incompatible member. Every caller here
 * passes one, so the alternative was five casts at the call sites, each of them
 * throwing away the argument's type to satisfy a constraint this function never
 * needed: it only reads entries off the object.
 */
function cleanParams<T extends object>(params: T | undefined): Record<string, unknown> {
  if (!params) return {};

  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'string' && !value.trim()) return false;
      return true;
    }),
  );
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;

  const numericValue = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(numericValue) ? numericValue : null;
}

function numberOrZero(value: unknown): number {
  return numberOrNull(value) ?? 0;
}

function cleanPaystackSubaccountPayload(payload: PaystackSubaccountUpdatePayload): PaystackSubaccountUpdatePayload {
  const trimOrNull = (value: string | null | undefined) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  };

  return {
    paystackSubaccountCode: trimOrNull(payload.paystackSubaccountCode),
    paystackSubaccountId: trimOrNull(payload.paystackSubaccountId),
    paystackSubaccountStatus: payload.paystackSubaccountStatus,
    paystackSplitPercentage: numberOrNull(payload.paystackSplitPercentage),
    paystackVerifiedAt: trimOrNull(payload.paystackVerifiedAt),
    paystackCreatedAt: trimOrNull(payload.paystackCreatedAt),
    paystackLastSyncAt: trimOrNull(payload.paystackLastSyncAt),
  };
}

function mapAdminUser(response: any): AdminUser {
  return {
    id: String(response.id),
    name: response.name,
    username: response.username,
    email: response.email,
    roles: response.roles ?? [],
    phone: response.phone ?? null,
    phoneVerified: response.phoneVerified ?? null,
    emailVerified: response.emailVerified ?? null,
    enabled: response.enabled,
    accountNonLocked: response.accountNonLocked,
    accountNonExpired: response.accountNonExpired,
    credentialsNonExpired: response.credentialsNonExpired,
    onboardingsStep: response.onboardingsStep ?? null,
    faceLiveness: response.faceLiveness ?? null,
    attachments: response.attachments ?? {},
  };
}

function mapAdminFaceLivenessReview(response: any): AdminFaceLivenessReview {
  return {
    userId: String(response?.userId ?? response?.user?.id ?? ''),
    user: mapAdminUser(response?.user ?? {}),
    status: response?.status ?? 'PENDING',
    reasonCode: response?.reasonCode ?? null,
    reviewNote: response?.reviewNote ?? null,
    reviewedByUserId: response?.reviewedByUserId ? String(response.reviewedByUserId) : null,
    reviewedByEmail: response?.reviewedByEmail ?? null,
    reviewedAt: response?.reviewedAt ?? null,
    faceLivenessComplete: Boolean(response?.faceLivenessComplete),
    straightPhotoPresent: Boolean(response?.straightPhotoPresent),
    leftPhotoPresent: Boolean(response?.leftPhotoPresent),
    rightPhotoPresent: Boolean(response?.rightPhotoPresent),
    latestFacePhotoUploadedAt: response?.latestFacePhotoUploadedAt ?? null,
  };
}

function mapDashboardRange(response: any): AdminEngagementDashboard['range'] {
  return {
    fromDate: response?.fromDate ?? '',
    toDate: response?.toDate ?? '',
    from: response?.from ?? '',
    to: response?.to ?? '',
    zoneId: response?.zoneId ?? 'Africa/Johannesburg',
  };
}

function mapFlowDashboardMetrics(response: any): AdminEngagementDashboard['instantRequests'] {
  return {
    requested: numberOrZero(response?.requested),
    accepted: numberOrZero(response?.accepted),
    notAccepted: numberOrZero(response?.notAccepted),
    completed: numberOrZero(response?.completed),
    stillAssigned: numberOrZero(response?.stillAssigned),
    overdue: numberOrZero(response?.overdue),
    acceptanceRatePercent: numberOrZero(response?.acceptanceRatePercent),
    completionRatePercent: numberOrZero(response?.completionRatePercent),
  };
}

function mapRevenueDashboardMetrics(response: any): AdminEngagementDashboard['revenue'] {
  return {
    grossCaptured: numberOrZero(response?.grossCaptured),
    grossFare: numberOrZero(response?.grossFare),
    refunded: numberOrZero(response?.refunded),
    driverPayout: numberOrZero(response?.driverPayout),
    platformCommission: numberOrZero(response?.platformCommission),
    averageCommissionPerCompletedTrip: numberOrZero(response?.averageCommissionPerCompletedTrip),
    currency: response?.currency ?? 'ZAR',
  };
}

function mapEnrollmentDashboardMetrics(response: any): AdminEngagementDashboard['users'] {
  return {
    enrolled: numberOrZero(response?.enrolled),
    active: numberOrZero(response?.active),
    deleted: numberOrZero(response?.deleted),
    completedOnboarding: numberOrZero(response?.completedOnboarding),
    stillOnboarding: numberOrZero(response?.stillOnboarding),
  };
}

function mapDriverDashboardMetrics(response: any): AdminEngagementDashboard['drivers'] {
  return {
    enrolled: numberOrZero(response?.enrolled),
    active: numberOrZero(response?.active),
    deleted: numberOrZero(response?.deleted),
    successfullyOnboarded: numberOrZero(response?.successfullyOnboarded),
    stillOnboarding: numberOrZero(response?.stillOnboarding),
    onlineNow: numberOrZero(response?.onlineNow),
  };
}

function mapEdgeCaseMetrics(response: any): AdminEngagementDashboard['edgeCases'] {
  return {
    staleAssignedBookings: numberOrZero(response?.staleAssignedBookings),
    searchingPastExpiry: numberOrZero(response?.searchingPastExpiry),
    acceptedWithoutDriver: numberOrZero(response?.acceptedWithoutDriver),
    completedWithoutCommission: numberOrZero(response?.completedWithoutCommission),
    capturedButNotCompleted: numberOrZero(response?.capturedButNotCompleted),
    cancelledAfterCapture: numberOrZero(response?.cancelledAfterCapture),
    duplicateTripRows: numberOrZero(response?.duplicateTripRows),
  };
}

export function mapAdminEngagementDashboard(response: any): AdminEngagementDashboard {
  return {
    generatedAt: response?.generatedAt ?? '',
    range: mapDashboardRange(response?.range),
    kpis: Array.isArray(response?.kpis)
      ? response.kpis.map((item: any) => ({
          key: item?.key ?? '',
          label: item?.label ?? '',
          value: numberOrZero(item?.value),
          unit: item?.unit ?? '',
        }))
      : [],
    instantRequests: mapFlowDashboardMetrics(response?.instantRequests),
    assignmentBookings: mapFlowDashboardMetrics(response?.assignmentBookings),
    revenue: mapRevenueDashboardMetrics(response?.revenue),
    users: mapEnrollmentDashboardMetrics(response?.users),
    drivers: mapDriverDashboardMetrics(response?.drivers),
    bookingTrend: Array.isArray(response?.bookingTrend)
      ? response.bookingTrend.map((item: any) => ({ period: item?.period ?? '', value: numberOrZero(item?.value) }))
      : [],
    revenueTrend: Array.isArray(response?.revenueTrend)
      ? response.revenueTrend.map((item: any) => ({ period: item?.period ?? '', value: numberOrZero(item?.value) }))
      : [],
    commissionTrend: Array.isArray(response?.commissionTrend)
      ? response.commissionTrend.map((item: any) => ({ period: item?.period ?? '', value: numberOrZero(item?.value) }))
      : [],
    bookingsByStatus: Array.isArray(response?.bookingsByStatus)
      ? response.bookingsByStatus.map((item: any) => ({ label: item?.label ?? 'UNKNOWN', value: numberOrZero(item?.value) }))
      : [],
    bookingsByFlow: Array.isArray(response?.bookingsByFlow)
      ? response.bookingsByFlow.map((item: any) => ({ label: item?.label ?? 'UNKNOWN', value: numberOrZero(item?.value) }))
      : [],
    driverRevenue: Array.isArray(response?.driverRevenue)
      ? response.driverRevenue.map((item: any) => ({
          driverId: item?.driverId ? String(item.driverId) : '',
          driverName: item?.driverName ?? null,
          completedTrips: numberOrZero(item?.completedTrips),
          grossCaptured: numberOrZero(item?.grossCaptured),
          driverPayout: numberOrZero(item?.driverPayout),
          platformCommission: numberOrZero(item?.platformCommission),
        }))
      : [],
    edgeCases: mapEdgeCaseMetrics(response?.edgeCases),
    warnings: Array.isArray(response?.warnings) ? response.warnings.map(String) : [],
  };
}

function mapDeclaredLoadSummary(response: any): AdminDeclaredLoadDashboard['summary'] {
  return {
    declaredLoads: numberOrZero(response?.declaredLoads),
    completedLoads: numberOrZero(response?.completedLoads),
    activeLoads: numberOrZero(response?.activeLoads),
    cancelledLoads: numberOrZero(response?.cancelledLoads),
    noProgressLoads: numberOrZero(response?.noProgressLoads),
    uniqueDrivers: numberOrZero(response?.uniqueDrivers),
    uniqueDeclaredCustomers: numberOrZero(response?.uniqueDeclaredCustomers),
    newCustomersCreated: numberOrZero(response?.newCustomersCreated),
    existingCustomersReused: numberOrZero(response?.existingCustomersReused),
    trackingLinksOpened: numberOrZero(response?.trackingLinksOpened),
    totalTrackingLinkOpens: numberOrZero(response?.totalTrackingLinkOpens),
    trackingOpenRate: numberOrZero(response?.trackingOpenRate),
    completionRate: numberOrZero(response?.completionRate),
    newCustomerRate: numberOrZero(response?.newCustomerRate),
    grossDeclaredFare: numberOrZero(response?.grossDeclaredFare),
    agreedFareTotal: numberOrZero(response?.agreedFareTotal),
    systemEstimateTotal: numberOrZero(response?.systemEstimateTotal),
    estimatedDriverPayout: numberOrZero(response?.estimatedDriverPayout),
  };
}

function mapDeclaredLoadDriverRow(response: any): AdminDeclaredLoadDashboard['driverRows'][number] {
  return {
    driverId: String(response?.driverId ?? ''),
    driverName: response?.driverName ?? null,
    driverEmail: response?.driverEmail ?? null,
    driverPhone: response?.driverPhone ?? null,
    declaredLoads: numberOrZero(response?.declaredLoads),
    completedLoads: numberOrZero(response?.completedLoads),
    activeLoads: numberOrZero(response?.activeLoads),
    cancelledLoads: numberOrZero(response?.cancelledLoads),
    noProgressLoads: numberOrZero(response?.noProgressLoads),
    uniqueDeclaredCustomers: numberOrZero(response?.uniqueDeclaredCustomers),
    newCustomersCreated: numberOrZero(response?.newCustomersCreated),
    existingCustomersReused: numberOrZero(response?.existingCustomersReused),
    trackingLinksOpened: numberOrZero(response?.trackingLinksOpened),
    totalTrackingLinkOpens: numberOrZero(response?.totalTrackingLinkOpens),
    trackingOpenRate: numberOrZero(response?.trackingOpenRate),
    completionRate: numberOrZero(response?.completionRate),
    newCustomerRate: numberOrZero(response?.newCustomerRate),
    grossDeclaredFare: numberOrZero(response?.grossDeclaredFare),
    agreedFareTotal: numberOrZero(response?.agreedFareTotal),
    systemEstimateTotal: numberOrZero(response?.systemEstimateTotal),
    estimatedDriverPayout: numberOrZero(response?.estimatedDriverPayout),
    firstDeclaredAt: response?.firstDeclaredAt ?? null,
    lastDeclaredAt: response?.lastDeclaredAt ?? null,
    lastTrackingOpenedAt: response?.lastTrackingOpenedAt ?? null,
    eligibilityStatus: response?.eligibilityStatus ?? 'NOT_ELIGIBLE',
    reviewFlags: Array.isArray(response?.reviewFlags) ? response.reviewFlags.map(String) : [],
    countsByStatus: response?.countsByStatus && typeof response.countsByStatus === 'object'
      ? Object.fromEntries(Object.entries(response.countsByStatus).map(([key, value]) => [key, numberOrZero(value)]))
      : {},
  };
}

export function mapAdminDeclaredLoadDashboard(response: any): AdminDeclaredLoadDashboard {
  return {
    generatedAt: response?.generatedAt ?? '',
    range: {
      fromDate: response?.range?.fromDate ?? '',
      toDate: response?.range?.toDate ?? '',
      zoneId: response?.range?.zoneId ?? 'Africa/Johannesburg',
    },
    summary: mapDeclaredLoadSummary(response?.summary),
    driverRows: Array.isArray(response?.driverRows) ? response.driverRows.map(mapDeclaredLoadDriverRow) : [],
    warnings: Array.isArray(response?.warnings) ? response.warnings.map(String) : [],
  };
}

function mapAdminDeclaredLoadListItem(response: any): AdminDeclaredLoadListItem {
  return {
    bookingId: String(response?.bookingId ?? ''),
    tripId: String(response?.tripId ?? ''),
    status: response?.status ?? null,
    declaredAt: response?.declaredAt ?? null,
    scheduledAt: response?.scheduledAt ?? null,
    declaredByDriverId: response?.declaredByDriverId ? String(response.declaredByDriverId) : null,
    driverId: response?.driverId ? String(response.driverId) : null,
    driverName: response?.driverName ?? null,
    driverEmail: response?.driverEmail ?? null,
    driverPhone: response?.driverPhone ?? null,
    declaredCustomerName: response?.declaredCustomerName ?? null,
    declaredCustomerEmail: response?.declaredCustomerEmail ?? null,
    declaredCustomerUserId: response?.declaredCustomerUserId ? String(response.declaredCustomerUserId) : null,
    declaredCustomerCreated:
      typeof response?.declaredCustomerCreated === 'boolean' ? response.declaredCustomerCreated : null,
    requesterUserId: response?.requesterUserId ? String(response.requesterUserId) : null,
    sourceAddress: response?.sourceAddress ?? null,
    destinationAddress: response?.destinationAddress ?? null,
    vehicleType: response?.vehicleType ?? null,
    agreedFare: numberOrNull(response?.agreedFare),
    estimatedFare: numberOrNull(response?.estimatedFare),
    fareAmount: numberOrNull(response?.fareAmount),
    driverPayout: numberOrNull(response?.driverPayout),
    fareSource: response?.fareSource ?? null,
    currency: response?.currency ?? null,
    paymentStatus: response?.paymentStatus ?? null,
    paymentState: response?.paymentState ?? null,
    collectionSource: response?.collectionSource ?? null,
    declarationSource: response?.declarationSource ?? null,
    trackingLinkOpenCount: numberOrZero(response?.trackingLinkOpenCount),
    trackingLinkLastOpenedAt: response?.trackingLinkLastOpenedAt ?? null,
    trackingLinkOpened: Boolean(response?.trackingLinkOpened),
    acceptedAt: response?.acceptedAt ?? null,
    onRouteToPickupAt: response?.onRouteToPickupAt ?? null,
    pickedUpAt: response?.pickedUpAt ?? null,
    enrouteAt: response?.enrouteAt ?? null,
    deliveredAt: response?.deliveredAt ?? null,
    completedAt: response?.completedAt ?? null,
    cancelledAt: response?.cancelledAt ?? null,
    cancelledBy: response?.cancelledBy ?? null,
    cancelReason: response?.cancelReason ?? null,
  };
}

function mapAdminDriverCampaign(response: any): AdminDriverCampaign {
  return {
    campaignId: String(response?.campaignId ?? ''),
    code: response?.code ?? '',
    name: response?.name ?? '',
    description: response?.description ?? null,
    campaignType: response?.campaignType ?? 'DECLARED_LOAD_FOUNDING_INCENTIVE',
    status: response?.status ?? 'DRAFT',
    startsAt: response?.startsAt ?? null,
    endsAt: response?.endsAt ?? null,
    inclusiveEndDate: Boolean(response?.inclusiveEndDate),
    currency: response?.currency ?? 'ZAR',
    budgetAmount: numberOrNull(response?.budgetAmount),
    maxParticipants: numberOrNull(response?.maxParticipants),
    maxQualifyingBookingsPerDriver: numberOrNull(response?.maxQualifyingBookingsPerDriver),
    rewardPerQualifyingBooking: numberOrNull(response?.rewardPerQualifyingBooking),
    maxRewardPerDriver: numberOrNull(response?.maxRewardPerDriver),
    minQualifyingDistanceKm: numberOrNull(response?.minQualifyingDistanceKm),
    homeBaseTown: response?.homeBaseTown ?? null,
    homeBaseProvince: response?.homeBaseProvince ?? null,
    homeBaseCountry: response?.homeBaseCountry ?? null,
    enrollmentPriority: numberOrZero(response?.enrollmentPriority),
    replacementSlotsAllowedDuringCampaign: Boolean(response?.replacementSlotsAllowedDuringCampaign),
    createdByAdminId: response?.createdByAdminId ? String(response.createdByAdminId) : null,
    createdByAdminEmail: response?.createdByAdminEmail ?? null,
    notes: response?.notes ?? null,
    metadataJson: response?.metadataJson ?? null,
    approvedParticipants: numberOrZero(response?.approvedParticipants),
    consumedSlots: numberOrZero(response?.consumedSlots),
    remainingSlots: numberOrNull(response?.remainingSlots),
    createdAt: response?.createdAt ?? null,
    updatedAt: response?.updatedAt ?? null,
  };
}

function mapAdminCustomerPromotion(response: any): AdminCustomerPromotion {
  return {
    campaignId: String(response?.campaignId ?? ''),
    code: response?.code ?? '',
    title: response?.title ?? '',
    description: response?.description ?? null,
    status: response?.status ?? 'DRAFT',
    discountPercent: numberOrZero(response?.discountPercent),
    maxRedemptions: numberOrZero(response?.maxRedemptions),
    localDistanceKm: numberOrZero(response?.localDistanceKm),
    startsAt: response?.startsAt ?? null,
    endsAt: response?.endsAt ?? null,
    assignOnSignup: Boolean(response?.assignOnSignup),
    createdByAdminId: response?.createdByAdminId ? String(response.createdByAdminId) : null,
    createdByAdminEmail: response?.createdByAdminEmail ?? null,
    updatedByAdminId: response?.updatedByAdminId ? String(response.updatedByAdminId) : null,
    updatedByAdminEmail: response?.updatedByAdminEmail ?? null,
    createdAt: response?.createdAt ?? null,
    updatedAt: response?.updatedAt ?? null,
  };
}

function mapAdminDriverCampaignEnrollment(response: any): AdminDriverCampaignEnrollment {
  return {
    enrollmentId: String(response?.enrollmentId ?? ''),
    campaignId: String(response?.campaignId ?? ''),
    campaignCode: response?.campaignCode ?? null,
    campaignName: response?.campaignName ?? null,
    campaignType: response?.campaignType ?? null,
    driverId: String(response?.driverId ?? ''),
    driverName: response?.driverName ?? null,
    driverEmail: response?.driverEmail ?? null,
    driverPhone: response?.driverPhone ?? null,
    homeBaseTown: response?.homeBaseTown ?? null,
    homeBaseProvince: response?.homeBaseProvince ?? null,
    homeBaseCountry: response?.homeBaseCountry ?? null,
    status: response?.status ?? 'APPROVED',
    enrolledAt: response?.enrolledAt ?? null,
    approvedAt: response?.approvedAt ?? null,
    suspendedAt: response?.suspendedAt ?? null,
    completedAt: response?.completedAt ?? null,
    removedAt: response?.removedAt ?? null,
    slotConsumed: Boolean(response?.slotConsumed),
    slotReleasedAt: response?.slotReleasedAt ?? null,
    enrolledByAdminId: response?.enrolledByAdminId ? String(response.enrolledByAdminId) : null,
    enrolledByAdminEmail: response?.enrolledByAdminEmail ?? null,
    statusReason: response?.statusReason ?? null,
    source: response?.source ?? null,
    metadataJson: response?.metadataJson ?? null,
  };
}

function mapAdminDriverCampaignEnrollmentBatch(response: any): AdminDriverCampaignEnrollmentBatch {
  return {
    requestedDrivers: numberOrZero(response?.requestedDrivers),
    enrolledDrivers: numberOrZero(response?.enrolledDrivers),
    skippedDrivers: numberOrZero(response?.skippedDrivers),
    enrollments: Array.isArray(response?.enrollments)
      ? response.enrollments.map(mapAdminDriverCampaignEnrollment)
      : [],
    skipped: Array.isArray(response?.skipped) ? response.skipped.map(String) : [],
  };
}

function mapAdminDriverCampaignSummary(response: any): AdminDriverCampaignSummary {
  return {
    enrolled: Boolean(response?.enrolled),
    campaignId: response?.campaignId ? String(response.campaignId) : null,
    campaignCode: response?.campaignCode ?? null,
    campaignName: response?.campaignName ?? null,
    campaignType: response?.campaignType ?? null,
    campaignStatus: response?.campaignStatus ?? null,
    startsAt: response?.startsAt ?? null,
    endsAt: response?.endsAt ?? null,
    enrollmentId: response?.enrollmentId ? String(response.enrollmentId) : null,
    enrollmentStatus: response?.enrollmentStatus ?? null,
    driverId: String(response?.driverId ?? ''),
    driverName: response?.driverName ?? null,
    reservedDeliveries: numberOrZero(response?.reservedDeliveries),
    qualifiedDeliveries: numberOrZero(response?.qualifiedDeliveries),
    rejectedDeliveries: numberOrZero(response?.rejectedDeliveries),
    maxQualifyingDeliveries: numberOrZero(response?.maxQualifyingDeliveries),
    remainingDeliveries: numberOrZero(response?.remainingDeliveries),
    earnedAmount: numberOrZero(response?.earnedAmount),
    paidAmount: numberOrZero(response?.paidAmount),
    adjustmentAmount: numberOrZero(response?.adjustmentAmount),
    reversalAmount: numberOrZero(response?.reversalAmount),
    outstandingAmount: numberOrZero(response?.outstandingAmount),
    maxRewardPerDriver: numberOrNull(response?.maxRewardPerDriver),
    currency: response?.currency ?? 'ZAR',
  };
}

function mapAdminDriverCampaignDelivery(response: any): AdminDriverCampaignDelivery {
  return {
    usageId: String(response?.usageId ?? ''),
    campaignId: String(response?.campaignId ?? ''),
    campaignCode: response?.campaignCode ?? null,
    bookingId: response?.bookingId ? String(response.bookingId) : null,
    tripId: response?.tripId ? String(response.tripId) : null,
    driverId: String(response?.driverId ?? ''),
    usageType: response?.usageType ?? null,
    status: response?.status ?? 'RESERVED',
    qualifyingDistanceKm: numberOrNull(response?.qualifyingDistanceKm),
    paymentWaived: Boolean(response?.paymentWaived),
    paymentWaivedAt: response?.paymentWaivedAt ?? null,
    qualifiedAt: response?.qualifiedAt ?? null,
    rejectedAt: response?.rejectedAt ?? null,
    rejectionReason: response?.rejectionReason ?? null,
    earnedAmount: numberOrZero(response?.earnedAmount),
    currency: response?.currency ?? 'ZAR',
  };
}

function mapAdminDriverCampaignLedgerTransaction(response: any): AdminDriverCampaignLedgerTransaction {
  return {
    transactionId: String(response?.transactionId ?? ''),
    campaignId: String(response?.campaignId ?? ''),
    campaignCode: response?.campaignCode ?? null,
    enrollmentId: response?.enrollmentId ? String(response.enrollmentId) : null,
    usageId: response?.usageId ? String(response.usageId) : null,
    bookingId: response?.bookingId ? String(response.bookingId) : null,
    tripId: response?.tripId ? String(response.tripId) : null,
    driverId: String(response?.driverId ?? ''),
    transactionType: response?.transactionType ?? 'EARNED',
    status: response?.status ?? 'POSTED',
    amount: numberOrZero(response?.amount),
    currency: response?.currency ?? 'ZAR',
    reason: response?.reason ?? null,
    reference: response?.reference ?? null,
    createdByActorId: response?.createdByActorId ? String(response.createdByActorId) : null,
    createdByActorEmail: response?.createdByActorEmail ?? null,
    createdByActorRole: response?.createdByActorRole ?? null,
    idempotencyKey: response?.idempotencyKey ?? null,
    eventAt: response?.eventAt ?? null,
  };
}

function mapAdminPaymentIntent(response: any): AdminPaymentIntent {
  return {
    publicId: String(response?.publicId ?? ''),
    bookingOwnerType: response?.bookingOwnerType ?? null,
    bookingOwnerId: response?.bookingOwnerId ?? null,
    tripGroupId: response?.tripGroupId ?? null,
    primaryBookingId: response?.primaryBookingId ?? null,
    userId: response?.userId ? String(response.userId) : null,
    customerFullName: response?.customerFullName ?? response?.customerName ?? null,
    customerEmail: response?.customerEmail ?? null,
    customerMobileE164: response?.customerMobileE164 ?? response?.customerMobile ?? null,
    driverId: response?.driverId ? String(response.driverId) : null,
    driverName: response?.driverName ?? null,
    driverEmail: response?.driverEmail ?? null,
    paymentCustomerPublicId: response?.paymentCustomerPublicId ? String(response.paymentCustomerPublicId) : null,
    paymentMethodPublicId: response?.paymentMethodPublicId ? String(response.paymentMethodPublicId) : null,
    paymentMethodType: response?.paymentMethodType ?? null,
    provider: response?.provider ?? null,
    providerIntentId: response?.providerIntentId ?? null,
    currency: response?.currency ?? null,
    quotedAmount: numberOrNull(response?.quotedAmount),
    authorizedAmount: numberOrNull(response?.authorizedAmount),
    capturedAmount: numberOrNull(response?.capturedAmount),
    refundedAmount: numberOrNull(response?.refundedAmount),
    grossAmount: numberOrNull(response?.grossAmount),
    platformFeeAmount: numberOrNull(response?.platformFeeAmount),
    gatewayFeeAmount: numberOrNull(response?.gatewayFeeAmount),
    driverNetAmount: numberOrNull(response?.driverNetAmount),
    collectionSource: response?.collectionSource ?? null,
    manualCollectionStatus: response?.manualCollectionStatus ?? null,
    agreedAmount: numberOrNull(response?.agreedAmount),
    manuallyCollectedAmount: numberOrNull(response?.manuallyCollectedAmount),
    outstandingAmount: numberOrNull(response?.outstandingAmount),
    manualCollectionAuthorizedAt: response?.manualCollectionAuthorizedAt ?? null,
    payoutTargetType: response?.payoutTargetType ?? null,
    payoutTargetId: response?.payoutTargetId ? String(response.payoutTargetId) : null,
    paystackSubaccountCode: response?.paystackSubaccountCode ?? null,
    paystackSplitPercentage: numberOrNull(response?.paystackSplitPercentage),
    splitResolvedAt: response?.splitResolvedAt ?? null,
    splitAppliedAt: response?.splitAppliedAt ?? null,
    splitStatus: response?.splitStatus ?? null,
    paymentState: response?.paymentState ?? null,
    refundState: response?.refundState ?? null,
    payoutState: response?.payoutState ?? null,
    failureState: response?.failureState ?? null,
    requiresCustomerAction: Boolean(response?.requiresCustomerAction),
    authExpiresAt: response?.authExpiresAt ?? null,
    capturedAt: response?.capturedAt ?? null,
  };
}

function mapAdminPayout(response: any): AdminPayout {
  return {
    publicId: String(response?.publicId ?? ''),
    paymentIntentPublicId: response?.paymentIntentPublicId ? String(response.paymentIntentPublicId) : null,
    driverId: response?.driverId ? String(response.driverId) : null,
    provider: response?.provider ?? null,
    providerPayoutId: response?.providerPayoutId ?? null,
    providerRecipientId: response?.providerRecipientId ?? null,
    providerTransferReference: response?.providerTransferReference ?? null,
    bankAccountRef: response?.bankAccountRef ?? null,
    amount: numberOrNull(response?.amount),
    currency: response?.currency ?? null,
    payoutState: response?.payoutState ?? null,
    availableOn: response?.availableOn ?? null,
    initiatedAt: response?.initiatedAt ?? null,
    settledAt: response?.settledAt ?? null,
    failedAt: response?.failedAt ?? null,
    failureCode: response?.failureCode ?? null,
    failureMessage: response?.failureMessage ?? null,
    retryCount: response?.retryCount == null ? null : Number(response.retryCount),
    nextRetryAt: response?.nextRetryAt ?? null,
    createdAt: response?.createdAt ?? null,
    updatedAt: response?.updatedAt ?? null,
  };
}

function mapAdminManualCollection(response: any): AdminManualCollection {
  return {
    publicId: String(response?.publicId ?? ''),
    paymentIntentPublicId: response?.paymentIntentPublicId ? String(response.paymentIntentPublicId) : null,
    driverId: response?.driverId ? String(response.driverId) : null,
    source: response?.source ?? null,
    status: response?.status ?? null,
    amount: numberOrNull(response?.amount),
    agreedAmountSnapshot: numberOrNull(response?.agreedAmountSnapshot),
    outstandingAfter: numberOrNull(response?.outstandingAfter),
    currency: response?.currency ?? null,
    proofReference: response?.proofReference ?? null,
    receivedBy: response?.receivedBy ?? null,
    declaredBy: response?.declaredBy ?? null,
    reason: response?.reason ?? null,
    loadingAuthorized: Boolean(response?.loadingAuthorized),
    collectedAt: response?.collectedAt ?? null,
    voidedAt: response?.voidedAt ?? null,
    voidReason: response?.voidReason ?? null,
    createdAt: response?.createdAt ?? null,
    updatedAt: response?.updatedAt ?? null,
  };
}

function mapAdminProviderEvent(response: any): AdminProviderEvent {
  return {
    publicId: String(response?.publicId ?? ''),
    provider: response?.provider ?? null,
    eventDirection: response?.eventDirection ?? null,
    eventType: response?.eventType ?? null,
    providerReference: response?.providerReference ?? null,
    paymentIntentPublicId: response?.paymentIntentPublicId ? String(response.paymentIntentPublicId) : null,
    requestJson: response?.requestJson ?? null,
    responseJson: response?.responseJson ?? null,
    httpStatus: response?.httpStatus == null ? null : Number(response.httpStatus),
    success: Boolean(response?.success),
    recordedAt: response?.recordedAt ?? null,
  };
}

function mapAdminWebhookEvent(response: any): AdminWebhookEvent {
  return {
    publicId: String(response?.publicId ?? ''),
    provider: response?.provider ?? null,
    providerEventId: response?.providerEventId ?? null,
    eventType: response?.eventType ?? null,
    signatureValid: Boolean(response?.signatureValid),
    payloadHash: response?.payloadHash ?? null,
    payloadJson: response?.payloadJson ?? null,
    receivedAt: response?.receivedAt ?? null,
    processedAt: response?.processedAt ?? null,
    processingState: response?.processingState ?? null,
    failureMessage: response?.failureMessage ?? null,
  };
}

function mapAdminLedgerEntry(response: any): AdminLedgerEntry {
  return {
    publicId: String(response?.publicId ?? ''),
    paymentIntentPublicId: response?.paymentIntentPublicId ? String(response.paymentIntentPublicId) : null,
    payoutPublicId: response?.payoutPublicId ? String(response.payoutPublicId) : null,
    entryType: response?.entryType ?? null,
    entrySide: response?.entrySide ?? null,
    amount: numberOrNull(response?.amount),
    currency: response?.currency ?? null,
    accountCode: response?.accountCode ?? null,
    referenceType: response?.referenceType ?? null,
    referenceId: response?.referenceId ?? null,
    description: response?.description ?? null,
    eventAt: response?.eventAt ?? null,
    createdAt: response?.createdAt ?? null,
  };
}

function mapAdminPaymentActionAudit(response: any): AdminPaymentActionAudit {
  return {
    publicId: String(response?.publicId ?? ''),
    actionType: response?.actionType ?? null,
    targetType: response?.targetType ?? null,
    targetPublicId: response?.targetPublicId ? String(response.targetPublicId) : null,
    paymentIntentPublicId: response?.paymentIntentPublicId ? String(response.paymentIntentPublicId) : null,
    payoutPublicId: response?.payoutPublicId ? String(response.payoutPublicId) : null,
    webhookEventPublicId: response?.webhookEventPublicId ? String(response.webhookEventPublicId) : null,
    requestedBy: response?.requestedBy ?? null,
    reason: response?.reason ?? null,
    idempotencyKey: response?.idempotencyKey ?? null,
    resultState: response?.resultState ?? null,
    resultMessage: response?.resultMessage ?? null,
    createdAt: response?.createdAt ?? null,
  };
}

function mapAdminPaymentIntentDetail(response: any): AdminPaymentIntentDetail {
  return {
    paymentIntent: mapAdminPaymentIntent(response?.paymentIntent ?? {}),
    payouts: Array.isArray(response?.payouts) ? response.payouts.map(mapAdminPayout) : [],
    manualCollections: Array.isArray(response?.manualCollections)
      ? response.manualCollections.map(mapAdminManualCollection)
      : [],
    providerEvents: Array.isArray(response?.providerEvents)
      ? response.providerEvents.map(mapAdminProviderEvent)
      : [],
    webhookEvents: Array.isArray(response?.webhookEvents)
      ? response.webhookEvents.map(mapAdminWebhookEvent)
      : [],
    ledgerEntries: Array.isArray(response?.ledgerEntries)
      ? response.ledgerEntries.map(mapAdminLedgerEntry)
      : [],
    adminActions: Array.isArray(response?.adminActions)
      ? response.adminActions.map(mapAdminPaymentActionAudit)
      : [],
  };
}

function mapAdminPayoutBatchRun(response: any): AdminPayoutBatchRun {
  return {
    batchPublicId: response?.batchPublicId ? String(response.batchPublicId) : null,
    batchReference: response?.batchReference ?? null,
    batchStatus: response?.batchStatus ?? null,
    payoutDay: response?.payoutDay ?? null,
    cutoffAt: response?.cutoffAt ?? null,
    scannedCount: Number(response?.scannedCount ?? 0),
    submittedCount: Number(response?.submittedCount ?? 0),
    paidCount: Number(response?.paidCount ?? 0),
    processingCount: Number(response?.processingCount ?? 0),
    failedCount: Number(response?.failedCount ?? 0),
    heldCount: Number(response?.heldCount ?? 0),
    skippedCount: Number(response?.skippedCount ?? 0),
    totalAmount: numberOrNull(response?.totalAmount),
    items: Array.isArray(response?.items)
      ? response.items.map((item: any) => ({
          payoutPublicId: item?.payoutPublicId ? String(item.payoutPublicId) : null,
          paymentIntentPublicId: item?.paymentIntentPublicId ? String(item.paymentIntentPublicId) : null,
          driverId: item?.driverId ? String(item.driverId) : null,
          result: item?.result ?? null,
          payoutState: item?.payoutState ?? null,
          providerTransferReference: item?.providerTransferReference ?? null,
          providerPayoutId: item?.providerPayoutId ?? null,
          note: item?.note ?? null,
        }))
      : [],
  };
}

function mapAdminPaymentActionPayload(payload: AdminPaymentActionPayload): AdminPaymentActionPayload {
  return {
    reason: payload.reason.trim(),
    ...(payload.requestedBy?.trim() ? { requestedBy: payload.requestedBy.trim() } : {}),
    ...(payload.idempotencyKey?.trim() ? { idempotencyKey: payload.idempotencyKey.trim() } : {}),
    ...(payload.amount !== undefined && payload.amount !== null ? { amount: payload.amount } : {}),
  };
}

function mapAdminManualCollectionPayload(payload: AdminManualCollectionPayload): AdminManualCollectionPayload {
  return {
    source: payload.source,
    amount: payload.amount,
    ...(payload.agreedAmount !== undefined ? { agreedAmount: payload.agreedAmount } : {}),
    ...(payload.authorizeLoading !== undefined ? { authorizeLoading: payload.authorizeLoading } : {}),
    ...(payload.collectedAt?.trim() ? { collectedAt: payload.collectedAt.trim() } : {}),
    ...(payload.proofReference?.trim() ? { proofReference: payload.proofReference.trim() } : {}),
    ...(payload.receivedBy?.trim() ? { receivedBy: payload.receivedBy.trim() } : {}),
    reason: payload.reason.trim(),
    ...(payload.idempotencyKey?.trim() ? { idempotencyKey: payload.idempotencyKey.trim() } : {}),
  };
}

function mapDriverListItem(response: any): AdminDriverListItem {
  return {
    driverId: String(response.driverId),
    name: response.name,
    email: response.email,
    phoneE164: response.phoneE164 ?? null,
    homeBaseTown: response.homeBaseTown ?? null,
    homeBaseProvince: response.homeBaseProvince ?? null,
    homeBaseCountry: response.homeBaseCountry ?? null,
    accountType: response.accountType ?? null,
    onboardingsStep: response.onboardingsStep ?? null,
    onboardingReviewStatus: response.onboardingReviewStatus ?? null,
    enabled: Boolean(response.enabled),
    emailVerified: Boolean(response.emailVerified),
    phoneVerified: Boolean(response.phoneVerified),
    onboardingSubmittedAt: response.onboardingSubmittedAt ?? null,
    gitStatus: response.gitStatus ?? null,
    riskMode: response.riskMode ?? null,
    gitCoverAmount: response.gitCoverAmount ?? null,
    gitPolicyExpiry: response.gitPolicyExpiry ?? null,
    dailyEarnings: response.dailyEarnings ?? 0,
    weeklyEarnings: response.weeklyEarnings ?? 0,
    monthlyEarnings: response.monthlyEarnings ?? 0,
    totalEarnings: response.totalEarnings ?? 0,
  };
}

function mapAdminEmailRecipientPreview(response: any): AdminEmailRecipientPreview {
  return {
    recipientType: response?.recipientType ?? 'USER',
    recipientId: String(response?.recipientId ?? ''),
    name: response?.name ?? null,
    email: response?.email ?? '',
    statusSummary: response?.statusSummary ?? null,
  };
}

function mapAdminEmailCampaignPreview(response: any): AdminEmailCampaignPreview {
  return {
    audienceType: response?.audienceType ?? 'USERS',
    estimatedRecipients: numberOrZero(response?.estimatedRecipients),
    sampleRecipients: Array.isArray(response?.sampleRecipients)
      ? response.sampleRecipients.map(mapAdminEmailRecipientPreview)
      : [],
    warnings: Array.isArray(response?.warnings) ? response.warnings.map(String) : [],
  };
}

function mapAdminEmailCampaign(response: any): AdminEmailCampaign {
  return {
    id: String(response?.id ?? ''),
    audienceType: response?.audienceType ?? 'USERS',
    sendMode: response?.sendMode ?? 'TEST',
    status: response?.status ?? 'QUEUED',
    subject: response?.subject ?? '',
    reason: response?.reason ?? null,
    createdByAdminId: response?.createdByAdminId ?? null,
    createdByAdminEmail: response?.createdByAdminEmail ?? null,
    recipientCount: numberOrZero(response?.recipientCount),
    sentCount: numberOrZero(response?.sentCount),
    failedCount: numberOrZero(response?.failedCount),
    skippedCount: numberOrZero(response?.skippedCount),
    failureMessage: response?.failureMessage ?? null,
    idempotencyKey: response?.idempotencyKey ?? null,
    startedAt: response?.startedAt ?? null,
    completedAt: response?.completedAt ?? null,
    created: response?.created ?? null,
    updated: response?.updated ?? null,
  };
}

function mapAdminEmailCampaignRecipient(response: any): AdminEmailCampaignRecipient {
  return {
    id: String(response?.id ?? ''),
    recipientType: response?.recipientType ?? 'USER',
    recipientId: String(response?.recipientId ?? ''),
    email: response?.email ?? '',
    name: response?.name ?? null,
    statusSnapshot: response?.statusSnapshot ?? null,
    sendStatus: response?.sendStatus ?? 'PENDING',
    providerMessageId: response?.providerMessageId ?? null,
    failureReason: response?.failureReason ?? null,
    sentAt: response?.sentAt ?? null,
  };
}

function mapAdminEmailCampaignDetail(response: any): AdminEmailCampaignDetail {
  return {
    campaign: mapAdminEmailCampaign(response?.campaign ?? {}),
    recipients: Array.isArray(response?.recipients) ? response.recipients.map(mapAdminEmailCampaignRecipient) : [],
  };
}

function mapDriverTownCoverage(response: any): AdminDriverTownCoverage {
  const vehicleTypeCounts =
    response?.vehicleTypeCounts && typeof response.vehicleTypeCounts === 'object'
      ? Object.entries(response.vehicleTypeCounts).reduce<Record<string, number>>((acc, [key, value]) => {
          acc[String(key)] = Number(value ?? 0);
          return acc;
        }, {})
      : {};

  return {
    town: response?.town ? String(response.town) : 'Unspecified',
    province: response?.province ?? null,
    country: response?.country ?? null,
    latitude: response?.latitude === undefined || response?.latitude === null ? null : Number(response.latitude),
    longitude: response?.longitude === undefined || response?.longitude === null ? null : Number(response.longitude),
    totalCount: Number(response?.totalCount ?? 0),
    approvedEnabledCount: Number(response?.approvedEnabledCount ?? 0),
    submittedPendingCount: Number(response?.submittedPendingCount ?? 0),
    onlineNowCount: Number(response?.onlineNowCount ?? 0),
    vehicleTypeCounts,
  };
}

function mapDriverReviewDetail(response: any): AdminDriverReviewDetail {
  return {
    driver: mapAdminDriverReviewDriver(response.driver),
    onboardingSubmittedAt: response.onboardingSubmittedAt ?? null,
    onboardingReviewStatus: response.onboardingReviewStatus ?? null,
    changeRequests: Array.isArray(response.changeRequests)
      ? response.changeRequests.map((item: any) => ({
          id: String(item.id),
          driverId: String(item.driverId),
          type: item.type ?? null,
          status: item.status ?? null,
          payloadJson: item.payloadJson ?? null,
          supportDocS3Key: item.supportDocS3Key ?? null,
          supportDocContentType: item.supportDocContentType ?? null,
          supportDocOriginalName: item.supportDocOriginalName ?? null,
          reviewedAt: item.reviewedAt ?? null,
          reviewedBy: item.reviewedBy ?? null,
          rejectionReason: item.rejectionReason ?? null,
          createdAt: item.createdAt ?? null,
          updatedAt: item.updatedAt ?? null,
        }))
      : [],
    reviewHistory: Array.isArray(response.reviewHistory)
      ? response.reviewHistory.map((item: any) => ({
          actionType: item.actionType ?? null,
          actorUserId: item.actorUserId ? String(item.actorUserId) : null,
          actorEmail: item.actorEmail ?? null,
          detailsJson: item.detailsJson ?? null,
          createdAt: item.createdAt ?? null,
        }))
      : [],
  };
}

function mapAdminDriverReviewDriver(response: any): AdminDriverReviewDetail['driver'] {
  return {
    id: String(response?.id ?? ''),
    name: response?.name ?? '',
    email: response?.email ?? '',
    phoneE164: response?.phoneE164 ?? null,
    roles: response?.roles ?? [],
    emailVerified: response?.emailVerified ?? null,
    phoneVerified: response?.phoneVerified ?? null,
    enabled: response?.enabled,
    accountNonLocked: response?.accountNonLocked,
    accountNonExpired: response?.accountNonExpired,
    credentialsNonExpired: response?.credentialsNonExpired,
    manualLocked: response?.manualLocked ?? null,
    earlyAdopter: response?.earlyAdopter ?? null,
    earlyAdopterEffectiveFrom: response?.earlyAdopterEffectiveFrom ?? null,
    earlyAdopterExpiresAt: response?.earlyAdopterExpiresAt ?? null,
    dangerousGoodsAccredited: response?.dangerousGoodsAccredited ?? null,
    dangerousGoodsReviewedAt: response?.dangerousGoodsReviewedAt ?? null,
    dangerousGoodsReviewedBy: response?.dangerousGoodsReviewedBy ? String(response.dangerousGoodsReviewedBy) : null,
    gitStatus: response?.gitStatus ?? null,
    riskMode: response?.riskMode ?? null,
    gitCoverAmount: response?.gitCoverAmount ?? null,
    gitPolicyExpiry: response?.gitPolicyExpiry ?? null,
    gitReviewNotes: response?.gitReviewNotes ?? null,
    gitReviewedAt: response?.gitReviewedAt ?? null,
    gitReviewedBy: response?.gitReviewedBy ? String(response.gitReviewedBy) : null,
    restrictedGoodsAcknowledgedAt: response?.restrictedGoodsAcknowledgedAt ?? null,
    ratings: response?.ratings ?? null,
    ratingCount: response?.ratingCount ?? null,
    onboardingsStep: response?.onboardingsStep ?? null,
    faceLiveness: response?.faceLiveness ?? null,
    accountType: response?.accountType ?? null,
    homeBaseLat: numberOrNull(response?.homeBaseLat),
    homeBaseLng: numberOrNull(response?.homeBaseLng),
    homeBaseTown: response?.homeBaseTown ?? null,
    homeBaseProvince: response?.homeBaseProvince ?? null,
    homeBaseCountry: response?.homeBaseCountry ?? null,
    companyDetails: response?.companyDetails ?? null,
    communicationPrefsResponse: response?.communicationPrefsResponse ?? null,
    vehicles: response?.vehicles ?? [],
    attachments: response?.attachments ?? {},
    bankDetails: response?.bankDetails ?? null,
  };
}

function mapAdminBusinessProfile(response: any): AdminBusinessProfile {
  return {
    id: String(response?.id ?? ''),
    ownerUserId: String(response?.ownerUserId ?? ''),
    ownerName: response?.ownerName ?? null,
    ownerEmail: response?.ownerEmail ?? null,
    ownerPhone: response?.ownerPhone ?? null,
    legalName: response?.legalName ?? '',
    tradingName: response?.tradingName ?? null,
    registrationNumber: response?.registrationNumber ?? null,
    vatNumber: response?.vatNumber ?? null,
    businessEmail: response?.businessEmail ?? null,
    businessPhone: response?.businessPhone ?? null,
    headOfficeAddress: response?.headOfficeAddress ?? null,
    representativeName: response?.representativeName ?? null,
    representativeEmail: response?.representativeEmail ?? null,
    verificationStatus: response?.verificationStatus ?? 'DRAFT',
    verificationNote: response?.verificationNote ?? null,
    submittedAt: response?.submittedAt ?? null,
    reviewedAt: response?.reviewedAt ?? null,
    createdDate: response?.createdDate ?? null,
    updatedDate: response?.updatedDate ?? null,
    branches: Array.isArray(response?.branches)
      ? response.branches.map((branch: any) => ({
          id: String(branch?.id ?? ''),
          branchName: branch?.branchName ?? '',
          address: branch?.address ?? '',
          latitude: branch?.latitude ?? null,
          longitude: branch?.longitude ?? null,
          contactEmail: branch?.contactEmail ?? null,
          contactPhone: branch?.contactPhone ?? null,
          defaultBranch: Boolean(branch?.defaultBranch),
        }))
      : [],
  };
}

function mapQueueSummary(response: any): AdminQueueSummary {
  return {
    submittedDriversCount: Number(response.submittedDriversCount ?? 0),
    pendingPayoutReviewsCount: Number(response.pendingPayoutReviewsCount ?? 0),
    latestSubmittedDrivers: Array.isArray(response.latestSubmittedDrivers)
      ? response.latestSubmittedDrivers.map(mapDriverListItem)
      : [],
  };
}

function mapCommissionSummary(response: any): AdminCommissionSummary {
  return {
    commissionToday: Number(response.commissionToday ?? 0),
    commissionThisWeek: Number(response.commissionThisWeek ?? 0),
    commissionThisMonth: Number(response.commissionThisMonth ?? 0),
    completedTripsCount: Number(response.completedTripsCount ?? 0),
  };
}

function mapCompanyPayoutReview(response: any): AdminCompanyPayoutReview {
  return {
    companyId: String(response?.companyId ?? ''),
    companyName: response?.companyName ?? null,
    primaryEmail: response?.primaryEmail ?? null,
    registrationNumber: response?.registrationNumber ?? null,
    payoutDetailsPresent: Boolean(response?.payoutDetailsPresent),
    payoutStatus: response?.payoutStatus ?? null,
    bankName: response?.bankName ?? null,
    maskedAccountNumber: response?.maskedAccountNumber ?? null,
    accountHolderName: response?.accountHolderName ?? null,
    branchCode: response?.branchCode ?? null,
    accountType: response?.accountType ?? null,
    bankLetterPresent: Boolean(response?.bankLetterPresent),
    bankLetterFileId: response?.bankLetterFileId ? String(response.bankLetterFileId) : null,
    bankLetterFileName: response?.bankLetterFileName ?? null,
    bankLetterStorageKey: response?.bankLetterStorageKey ?? null,
    bankLetterDownloadUrl: response?.bankLetterDownloadUrl ?? null,
    bankLetterUploadedAt: response?.bankLetterUploadedAt ?? null,
    paystackSubaccountCode: response?.paystackSubaccountCode ?? null,
    paystackSubaccountId: response?.paystackSubaccountId ?? null,
    paystackSubaccountStatus: response?.paystackSubaccountStatus ?? null,
    paystackSplitPercentage: numberOrNull(response?.paystackSplitPercentage),
    paystackVerifiedAt: response?.paystackVerifiedAt ?? null,
    paystackCreatedAt: response?.paystackCreatedAt ?? null,
    paystackLastSyncAt: response?.paystackLastSyncAt ?? null,
    updatedAt: response?.updatedAt ?? null,
    lastUpdatedByUserId: response?.lastUpdatedByUserId ? String(response.lastUpdatedByUserId) : null,
  };
}

function mapPageResponse<T>(response: any, mapper: (item: any) => T): {
  items: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
} {
  return {
    items: Array.isArray(response.content) ? response.content.map(mapper) : [],
    page: Number(response.number ?? 0),
    size: Number(response.size ?? 20),
    totalElements: Number(response.totalElements ?? 0),
    totalPages: Number(response.totalPages ?? 0),
  };
}

function mapConversation(response: any): AdminReviewConversation {
  return {
    conversationId: String(response.conversationId),
    bookingId: response.bookingId ?? null,
    lastMessageAt: response.lastMessageAt ?? null,
    participants: Array.isArray(response.participants) ? response.participants : [],
  };
}

function mapTripsSummary(response: any): AdminTripsSummary {
  return {
    countsByStatus:
      response && typeof response.countsByStatus === 'object' && response.countsByStatus
        ? Object.fromEntries(
            Object.entries(response.countsByStatus as Record<string, unknown>).map(([key, value]) => [
              key,
              Number(value ?? 0),
            ]),
          )
        : {},
    totalTrips: Number(response.totalTrips ?? 0),
    activeTrips: Number(response.activeTrips ?? 0),
    completedTrips: Number(response.completedTrips ?? 0),
    cancelledTrips: Number(response.cancelledTrips ?? 0),
    acceptedTripsToday: Number(response.acceptedTripsToday ?? 0),
    inProgressTripsToday: Number(response.inProgressTripsToday ?? 0),
    rejectedTripsToday: Number(response.rejectedTripsToday ?? 0),
  };
}

function mapTripListItem(response: any): AdminTripListItem {
  return {
    tripId: String(response.tripId ?? ''),
    tripGroupId: response.tripGroupId ?? response.tripId ?? null,
    representativeBookingId: response.representativeBookingId ?? null,
    status: response.status ?? null,
    flow: response.flow ?? null,
    createdAt: response.createdAt ?? null,
    scheduledAt: response.scheduledAt ?? null,
    sourceAddress: response.sourceAddress ?? null,
    destinationAddress: response.destinationAddress ?? null,
    requesterUserId: response.requesterUserId ?? null,
    requesterName: response.requesterName ?? null,
    requesterEmail: response.requesterEmail ?? null,
    requesterPhone: response.requesterPhone ?? null,
    activeDriverId: response.activeDriverId ?? null,
    activeDriverName: response.activeDriverName ?? null,
    activeDriverEmail: response.activeDriverEmail ?? null,
    activeDriverPhone: response.activeDriverPhone ?? null,
    fareAmount: response.fareAmount ?? null,
    estimatedFare: response.estimatedFare ?? null,
    commissionAmount: response.commissionAmount ?? null,
    driverPayout: response.driverPayout ?? null,
    currency: response.currency ?? null,
    vehicleType: response.vehicleType ?? null,
    paymentStatus: response.paymentStatus ?? null,
    paymentRef: response.paymentRef ?? response.providerIntentId ?? null,
    paymentProvider: response.paymentProvider ?? response.provider ?? null,
    cancelledBy: response.cancelledBy ?? null,
    cancelReason: response.cancelReason ?? null,
    bookingSource: response.bookingSource ?? null,
    bookingSourcePhone: response.bookingSourcePhone ?? response.whatsappPhoneNumber ?? null,
    driverCandidateCount: numberOrNull(response.driverCandidateCount),
    adminDispatchRequired: response.adminDispatchRequired ?? null,
  };
}

function mapAssistedDispatchQueueItem(response: any): AdminAssistedDispatchQueueItem {
  return {
    tripId: String(response?.tripId ?? ''),
    bookingId: String(response?.bookingId ?? ''),
    driverId: response?.driverId ?? null,
    driverName: response?.driverName ?? null,
    driverEmail: response?.driverEmail ?? null,
    driverPhone: response?.driverPhone ?? null,
    status: response?.status ?? null,
    scheduledAt: response?.scheduledAt ?? null,
    sourceAddress: response?.sourceAddress ?? null,
    destinationAddress: response?.destinationAddress ?? null,
    requesterUserId: response?.requesterUserId ?? null,
    requesterName: response?.requesterName ?? null,
    requesterPhone: response?.requesterPhone ?? response?.bookingSourcePhone ?? null,
    vehicleType: response?.vehicleType ?? null,
    pricingMode: response?.pricingMode ?? null,
    quoteStatus: response?.quoteStatus ?? null,
    estimatedFare: numberOrNull(response?.estimatedFare),
    fareAmount: numberOrNull(response?.fareAmount),
    currency: response?.currency ?? null,
    acceptanceSource: response?.acceptanceSource ?? null,
    acceptedByActorId: response?.acceptedByActorId ?? null,
    acceptedByActorRole: response?.acceptedByActorRole ?? null,
    assistedDispatchReason: response?.assistedDispatchReason ?? null,
    bookingSource: response?.bookingSource ?? null,
    bookingSourceChannel: response?.bookingSourceChannel ?? null,
    bookingSourceReference: response?.bookingSourceReference ?? null,
    driverCandidateCount: numberOrNull(response?.driverCandidateCount),
    driverCandidateIds: response?.driverCandidateIds ?? null,
    adminDispatchRequired: response?.adminDispatchRequired ?? null,
  };
}

function mapAssistedDispatchCandidate(response: any): AdminAssistedDispatchCandidate {
  return {
    driverId: String(response?.driverId ?? response?.id ?? ''),
    name: response?.name ?? null,
    avatarUrl: response?.avatarUrl ?? null,
    rating: numberOrNull(response?.rating),
    trips: numberOrNull(response?.trips),
    vehicleType: response?.vehicleType ?? null,
    distanceKm: numberOrNull(response?.distanceKm),
    etaToPickupMin: numberOrNull(response?.etaToPickupMin),
    lastActiveAgoMin: numberOrNull(response?.lastActiveAgoMin),
    status: response?.status ?? null,
    availableForRequestedSlot: response?.availableForRequestedSlot ?? null,
  };
}

function mapAssistedDispatchAssignResponse(response: any): AdminAssistedDispatchAssignResponse {
  return {
    tripId: String(response?.tripId ?? ''),
    bookingId: String(response?.bookingId ?? ''),
    driverId: response?.driverId ?? null,
    previousStatus: response?.previousStatus ?? null,
    status: response?.status ?? null,
    acceptanceSource: response?.acceptanceSource ?? null,
    acceptedByActorId: response?.acceptedByActorId ?? null,
    acceptedByActorRole: response?.acceptedByActorRole ?? null,
    acceptedAt: response?.acceptedAt ?? null,
    reason: response?.reason ?? null,
  };
}

function mapTripResolution(response: any): AdminTripResolutionResponse {
  return {
    tripId: String(response?.tripId ?? ''),
    bookingId: response?.bookingId ?? null,
    previousStatus: response?.previousStatus ?? null,
    status: response?.status ?? null,
    resolvedAt: response?.resolvedAt ?? null,
    resolvedBy: response?.resolvedBy ?? null,
    reason: response?.reason ?? null,
  };
}

function mapTripDetail(response: any): AdminTripDetail {
  return {
    tripId: String(response.tripId ?? ''),
    tripGroupId: response.tripGroupId ?? response.tripId ?? null,
    representativeBookingId: response.representativeBookingId ?? null,
    status: response.status ?? null,
    flow: response.flow ?? null,
    createdAt: response.createdAt ?? null,
    scheduledAt: response.scheduledAt ?? null,
    vehicleType: response.vehicleType ?? null,
    pickup: response.pickup ?? null,
    dropoff: response.dropoff ?? null,
    pickupContact: response.pickupContact ?? null,
    dropoffContact: response.dropoffContact ?? null,
    loadNotes: response.loadNotes ?? null,
    route: response.route ?? null,
    routeToPickup: response.routeToPickup ?? null,
    timeline: Array.isArray(response.timeline) ? response.timeline : [],
    proofOfDelivery: response.proofOfDelivery ?? null,
    requester: response.requester ?? null,
    activeDriver: response.activeDriver ?? null,
    estimatedFare: response.estimatedFare ?? null,
    fareAmount: response.fareAmount ?? null,
    commissionAmount: response.commissionAmount ?? null,
    driverPayout: response.driverPayout ?? null,
    currency: response.currency ?? null,
    paymentStatus: response.paymentStatus ?? null,
    paymentRef: response.paymentRef ?? response.providerIntentId ?? null,
    paymentProvider: response.paymentProvider ?? response.provider ?? null,
    paymentIntentPublicId: response.paymentIntentPublicId ? String(response.paymentIntentPublicId) : null,
    collectionSource: response.collectionSource ?? null,
    manualCollectionStatus: response.manualCollectionStatus ?? null,
    agreedAmountOverride: numberOrNull(response.agreedAmountOverride ?? response.agreedAmount),
    manuallyCollectedAmount: numberOrNull(response.manuallyCollectedAmount),
    outstandingAmount: numberOrNull(response.outstandingAmount),
    manualCollectionAuthorizedAt: response.manualCollectionAuthorizedAt ?? null,
    primaryBookingId: response.primaryBookingId ?? response.representativeBookingId ?? null,
    providerIntentId: response.providerIntentId ?? response.paymentRef ?? null,
    commissionPlanCode: response.commissionPlanCode ?? null,
    commissionRateBps: response.commissionRateBps ?? null,
    earlyAdopter: response.earlyAdopter ?? null,
    cancelledBy: response.cancelledBy ?? null,
    cancelReason: response.cancelReason ?? null,
    cancelledAt: response.cancelledAt ?? null,
    acceptanceSource: response.acceptanceSource ?? null,
    acceptedByActorId: response.acceptedByActorId ?? null,
    acceptedByActorRole: response.acceptedByActorRole ?? null,
    assistedDispatchReason: response.assistedDispatchReason ?? null,
    bookingSource: response.bookingSource ?? null,
    bookingSourcePhone: response.bookingSourcePhone ?? response.whatsappPhoneNumber ?? null,
    driverCandidateCount: numberOrNull(response.driverCandidateCount),
    driverCandidateIds: response.driverCandidateIds ?? null,
    adminDispatchRequired: response.adminDispatchRequired ?? null,
    declaredLoad: response.declaredLoad
      ? {
          declaredLoad: Boolean(response.declaredLoad.declaredLoad),
          declaredByDriverId: response.declaredLoad.declaredByDriverId ?? null,
          declaredAt: response.declaredLoad.declaredAt ?? null,
          declaredCustomerName: response.declaredLoad.declaredCustomerName ?? null,
          declaredCustomerEmail: response.declaredLoad.declaredCustomerEmail ?? null,
          declaredCustomerUserId: response.declaredLoad.declaredCustomerUserId ?? null,
          declaredCustomerCreated:
            typeof response.declaredLoad.declaredCustomerCreated === 'boolean'
              ? response.declaredLoad.declaredCustomerCreated
              : null,
          declarationSource: response.declaredLoad.declarationSource ?? null,
          fareSource: response.declaredLoad.fareSource ?? null,
        }
      : null,
    bookingRows: Array.isArray(response.bookingRows)
      ? response.bookingRows.map((row: any) => ({
          bookingId: row.bookingId ?? null,
          driverId: row.driverId ?? null,
          driverName: row.driverName ?? null,
          driverEmail: row.driverEmail ?? null,
          driverPhone: row.driverPhone ?? null,
          status: row.status ?? null,
          sentAt: row.sentAt ?? null,
          viewedAt: row.viewedAt ?? null,
          acceptedAt: row.acceptedAt ?? null,
          acceptanceSource: row.acceptanceSource ?? null,
          acceptedByActorId: row.acceptedByActorId ?? null,
          acceptedByActorRole: row.acceptedByActorRole ?? null,
          assistedDispatchReason: row.assistedDispatchReason ?? null,
          expiredAt: row.expiredAt ?? null,
          cancelledAt: row.cancelledAt ?? null,
          completedAt: row.completedAt ?? null,
        }))
      : [],
  };
}

function mapMessage(response: any): AdminReviewMessage {
  const attachments = Array.isArray(response.attachments)
    ? response.attachments.map((attachment: any) => ({
        id: attachment?.id ? String(attachment.id) : null,
        name: attachment?.name ?? null,
        fileName: attachment?.fileName ?? null,
        originalName: attachment?.originalName ?? null,
        originalFileName: attachment?.originalFileName ?? null,
        mimeType: attachment?.mimeType ?? null,
        contentType: attachment?.contentType ?? null,
        size: attachment?.size ?? null,
        url: attachment?.url ?? null,
        downloadUrl: attachment?.downloadUrl ?? null,
      }))
    : [];

  return {
    id: String(response.id),
    conversationId: response.conversationId ?? null,
    senderType: response.senderType ?? null,
    senderId: response.senderId ?? null,
    senderName: response.senderName ?? null,
    kind: response.kind ?? null,
    content: response.content ?? null,
    createdAt: response.createdAt ?? response.createdAtIso ?? null,
    createdAtIso: response.createdAtIso ?? null,
    deleted: response.deleted ?? null,
    fullyReadByRecipients: response.fullyReadByRecipients ?? null,
    recipientCount: response.recipientCount ?? null,
    recipientReadCount: response.recipientReadCount ?? null,
    attachments,
  };
}

function mapCompanyMatchingRefreshResponse(response: any): AdminCompanyMatchingRefreshResponse {
  return {
    scope: response?.scope ?? null,
    companyUuid: response?.companyUuid ?? null,
    driverId: response?.driverId ? String(response.driverId) : null,
    companiesEvaluated: Number(response?.companiesEvaluated ?? 0),
    driversEvaluated: Number(response?.driversEvaluated ?? 0),
    linkedCount: Number(response?.linkedCount ?? 0),
    pendingReviewCount: Number(response?.pendingReviewCount ?? 0),
    manualReviewCount: Number(response?.manualReviewCount ?? 0),
    affectedCompanyUuids: Array.isArray(response?.affectedCompanyUuids) ? response.affectedCompanyUuids : [],
  };
}

export function isAdminRole(roles: string[] | null | undefined): boolean {
  return (roles ?? []).some((role) => {
    const normalized = String(role).toUpperCase();
    return normalized === 'ROLE_ADMIN' || normalized === 'ADMIN' || normalized === 'ROLE_SUPER_ADMIN';
  });
}

export function isSuperAdminRole(roles: string[] | null | undefined): boolean {
  return (roles ?? []).some((role) => {
    const normalized = String(role).toUpperCase();
    return normalized === 'ROLE_SUPER_ADMIN' || normalized === 'SUPER_ADMIN';
  });
}

export async function fetchAdminUserById(userId: string, token: string): Promise<AdminUser> {
  try {
    const response = await api.get(`/user-management/api/users/${encodeURIComponent(userId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return mapAdminUser(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load admin user details.'));
  }
}

export async function verifyAdminEmail(email: string, code: string): Promise<{
  token?: string;
  accessToken?: string | null;
  refreshToken?: string | null;
  accessTokenExpiresAt?: string | null;
  refreshTokenExpiresAt?: string | null;
  user: AdminUser;
}> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/verify-email', {
      email,
      code,
      ...getWebSessionContext(),
    });
    const tokenBundle = normalizeTokenBundle(response.data);
    return {
      token: tokenBundle.accessToken ?? undefined,
      accessToken: tokenBundle.accessToken,
      refreshToken: tokenBundle.refreshToken,
      accessTokenExpiresAt: tokenBundle.accessTokenExpiresAt,
      refreshTokenExpiresAt: tokenBundle.refreshTokenExpiresAt,
      user: mapAdminUser(response.data?.userResponse ?? {}),
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Email verification failed.'));
  }
}

export async function adminSignIn(payload: {
  email: string;
  password: string;
}): Promise<{
  success: boolean;
  message: string;
  token?: string;
  accessToken?: string | null;
  refreshToken?: string | null;
  accessTokenExpiresAt?: string | null;
  refreshTokenExpiresAt?: string | null;
  user?: AdminUser;
  otpRequired: boolean;
  otpChannel?: string | null;
  maskedDestination?: string | null;
  challengeId?: string | null;
  challengeExpiresAt?: string | null;
  otpExpiresAt?: string | null;
}> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/admin/login', {
      ...payload,
      ...getWebSessionContext(),
    });
    const tokenBundle = normalizeTokenBundle(response.data);
    return {
      success: Boolean(response.data?.success),
      message: response.data?.message ?? '',
      token: tokenBundle.accessToken ?? undefined,
      accessToken: tokenBundle.accessToken,
      refreshToken: tokenBundle.refreshToken,
      accessTokenExpiresAt: tokenBundle.accessTokenExpiresAt,
      refreshTokenExpiresAt: tokenBundle.refreshTokenExpiresAt,
      user: response.data?.userResponse ? mapAdminUser(response.data.userResponse) : undefined,
      otpRequired: Boolean(response.data?.otpRequired),
      otpChannel: response.data?.otpChannel ?? null,
      maskedDestination: response.data?.maskedDestination ?? null,
      challengeId: response.data?.challengeId ?? null,
      challengeExpiresAt: response.data?.challengeExpiresAt ?? null,
      otpExpiresAt: response.data?.otpExpiresAt ?? null,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data;
      if (responseData && typeof responseData === 'object') {
        const data = responseData as Record<string, unknown>;
        const candidateMessage =
          (typeof data.message === 'string' && data.message) ||
          (typeof data.error === 'string' && data.error) ||
          (typeof data.details === 'string' && data.details) ||
          (typeof data.detail === 'string' && data.detail) ||
          '';
        const otpRequired =
          Boolean(data.otpRequired) ||
          Boolean(data.challengeId) ||
          /otp verification required/i.test(candidateMessage);

        if (otpRequired) {
          return {
            success: Boolean(data.success),
            message: candidateMessage || 'OTP verification required for admin login.',
            token: undefined,
            accessToken: null,
            refreshToken: null,
            accessTokenExpiresAt: null,
            refreshTokenExpiresAt: null,
            user: data.userResponse ? mapAdminUser(data.userResponse) : undefined,
            otpRequired: true,
            otpChannel: typeof data.otpChannel === 'string' ? data.otpChannel : null,
            maskedDestination:
              typeof data.maskedDestination === 'string' ? data.maskedDestination : null,
            challengeId: typeof data.challengeId === 'string' ? data.challengeId : null,
            challengeExpiresAt:
              typeof data.challengeExpiresAt === 'string' ? data.challengeExpiresAt : null,
            otpExpiresAt: typeof data.otpExpiresAt === 'string' ? data.otpExpiresAt : null,
          };
        }
      }
    }

    throw new Error(extractErrorMessage(error, 'Could not sign in as admin.'));
  }
}

export async function verifyAdminLoginOtp(payload: {
  challengeId: string;
  code: string;
}): Promise<{
  success: boolean;
  message: string;
  token?: string;
  accessToken?: string | null;
  refreshToken?: string | null;
  accessTokenExpiresAt?: string | null;
  refreshTokenExpiresAt?: string | null;
  user?: AdminUser;
}> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/admin/login/verify-otp', {
      ...payload,
      ...getWebSessionContext(),
    });
    const tokenBundle = normalizeTokenBundle(response.data);
    return {
      success: Boolean(response.data?.success),
      message: response.data?.message ?? '',
      token: tokenBundle.accessToken ?? undefined,
      accessToken: tokenBundle.accessToken,
      refreshToken: tokenBundle.refreshToken,
      accessTokenExpiresAt: tokenBundle.accessTokenExpiresAt,
      refreshTokenExpiresAt: tokenBundle.refreshTokenExpiresAt,
      user: response.data?.userResponse ? mapAdminUser(response.data.userResponse) : undefined,
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not verify the login code.'));
  }
}

export async function refreshAdminAuthSession(): Promise<string | null> {
  return refreshRoleSession('admin');
}

export async function logoutAdminAuthSession(): Promise<void> {
  await logoutRoleSession('admin');
  clearAdminAuthFlow();
}

export async function resendAdminLoginOtp(payload: {
  challengeId: string;
}): Promise<{
  success: boolean;
  message: string;
  otpRequired: boolean;
  otpChannel?: string | null;
  maskedDestination?: string | null;
  challengeId?: string | null;
  challengeExpiresAt?: string | null;
  otpExpiresAt?: string | null;
}> {
  try {
    const response = await publicApi.post('/auth-service/v1/auth/admin/login/resend-otp', payload);
    return {
      success: Boolean(response.data?.success),
      message: response.data?.message ?? '',
      otpRequired: Boolean(response.data?.otpRequired),
      otpChannel: response.data?.otpChannel ?? null,
      maskedDestination: response.data?.maskedDestination ?? null,
      challengeId: response.data?.challengeId ?? null,
      challengeExpiresAt: response.data?.challengeExpiresAt ?? null,
      otpExpiresAt: response.data?.otpExpiresAt ?? null,
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not resend the login code.'));
  }
}

export async function sendAdminPhoneOtp(userId: string, phoneE164: string): Promise<void> {
  try {
    await api.post('/user-management/api/driver-auth/phone/send-otp-v2', {
      userId,
      phoneE164,
    });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not send the phone verification code.'));
  }
}

export async function verifyAdminPhoneOtp(
  userId: string,
  phoneE164: string,
  code: string,
): Promise<AdminUser> {
  try {
    const response = await api.post('/user-management/api/driver-auth/phone/verify-otp-v2', {
      userId,
      phoneE164,
      code,
    });
    return mapAdminUser(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not verify your phone number.'));
  }
}

export async function uploadAdminFaceLiveness(
  userId: string,
  challengeType: 'STRAIGHT' | 'LEFT' | 'RIGHT',
  sequenceOrder: 1 | 2 | 3,
  file: Blob,
): Promise<AdminUser> {
  const formData = new FormData();
  formData.append('file', file, `admin_face_liveness_${challengeType.toLowerCase()}_${Date.now()}.jpg`);

  try {
    const response = await api.post(
      `/user-management/api/users/onboarding/files/face-liveness?driverId=${encodeURIComponent(
        userId,
      )}&challengeType=${challengeType}&sequenceOrder=${sequenceOrder}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000,
      },
    );

    return mapAdminUser(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not upload face verification images.'));
  }
}

export async function fetchAdminQueueSummary(): Promise<AdminQueueSummary> {
  try {
    const response = await api.get('/driver-management/api/admin/queue-summary');
    return mapQueueSummary(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the admin queue summary.'));
  }
}

export async function fetchAdminReviewQueue(params: Record<string, unknown>): Promise<{
  items: AdminDriverListItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/driver-management/api/admin/drivers/review-queue', { params });
    return mapPageResponse(response.data, mapDriverListItem);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the driver review queue.'));
  }
}

export async function fetchAdminFaceLivenessReviews(params: Record<string, unknown>): Promise<{
  items: AdminFaceLivenessReview[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/user-management/api/admin/face-liveness-reviews', {
      params: cleanParams(params),
    });
    return mapPageResponse(response.data, mapAdminFaceLivenessReview);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load face liveness reviews.'));
  }
}

export async function fetchAdminFaceLivenessReview(userId: string): Promise<AdminFaceLivenessReview> {
  try {
    const response = await api.get(`/user-management/api/admin/face-liveness-reviews/${encodeURIComponent(userId)}`);
    return mapAdminFaceLivenessReview(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load face liveness review details.'));
  }
}

export async function submitAdminFaceLivenessReviewDecision(
  userId: string,
  payload: {
    status: string;
    reasonCode?: string | null;
    reviewNote?: string | null;
  },
): Promise<AdminFaceLivenessReview> {
  try {
    const response = await api.post(
      `/user-management/api/admin/face-liveness-reviews/${encodeURIComponent(userId)}/decision`,
      {
        status: payload.status,
        ...(payload.reasonCode ? { reasonCode: payload.reasonCode } : {}),
        ...(payload.reviewNote?.trim() ? { reviewNote: payload.reviewNote.trim() } : {}),
      },
    );
    return mapAdminFaceLivenessReview(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save the face liveness review decision.'));
  }
}

export async function fetchAdminDrivers(params: Record<string, unknown>): Promise<{
  items: AdminDriverListItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/driver-management/api/admin/drivers', { params });
    return mapPageResponse(response.data, mapDriverListItem);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load admin drivers.'));
  }
}

export async function fetchAdminDriverTownCoverage(params: Record<string, unknown> = {}): Promise<AdminDriverTownCoverage[]> {
  try {
    const response = await api.get('/driver-management/api/admin/drivers/town-coverage', {
      params: cleanParams(params),
    });
    return Array.isArray(response.data) ? response.data.map(mapDriverTownCoverage) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load driver town coverage.'));
  }
}

export async function fetchAdminDriverReview(driverId: string): Promise<AdminDriverReviewDetail> {
  try {
    const response = await api.get(`/driver-management/api/admin/drivers/${encodeURIComponent(driverId)}/review`);
    return mapDriverReviewDetail(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the driver review detail.'));
  }
}

const ADMIN_DRIVER_ONBOARDING_ASSIST_BASE = '/driver-management/api/admin/drivers/onboarding-assist';

type AdminDriverAssistDriver = AdminDriverReviewDetail['driver'];

function mapAdminDriverAssistResponse(responseData: unknown, fallbackId?: string): AdminDriverAssistDriver {
  return mapAdminDriverReviewDriver(responseData ?? { id: fallbackId ?? '' });
}

export async function lookupAdminDriverOnboardingByEmail(email: string): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.get(`${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/lookup`, {
      params: { email: email.trim() },
    });
    return mapAdminDriverAssistResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not find a driver profile for that email.'));
  }
}

export async function startAdminDriverOnboarding(
  payload: AdminDriverOnboardingStartPayload,
): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.post(`${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/start`, cleanParams(payload));
    return mapAdminDriverAssistResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not start the assisted onboarding profile.'));
  }
}

export async function updateAdminDriverOnboardingOperatingTown(
  driverId: string,
  payload: AdminDriverOperatingTownPayload,
): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.put(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/operating-town`,
      cleanParams(payload),
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save the operating town.'));
  }
}

export async function updateAdminDriverOnboardingPhone(
  driverId: string,
  phoneE164: string,
): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.put(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/phone`,
      { phoneE164: phoneE164.trim() },
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save the driver phone number.'));
  }
}

export async function uploadAdminDriverOnboardingFaceLiveness(
  driverId: string,
  challengeType: 'STRAIGHT' | 'LEFT' | 'RIGHT',
  sequenceOrder: 1 | 2 | 3,
  file: File,
): Promise<AdminDriverAssistDriver> {
  const formData = new FormData();
  formData.append('challengeType', challengeType);
  formData.append('sequenceOrder', String(sequenceOrder));
  formData.append('file', file);

  try {
    const response = await api.post(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/face-liveness`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      },
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not upload the face liveness photo.'));
  }
}

export async function uploadAdminDriverOnboardingFile(
  driverId: string,
  fileType: string,
  file: File,
  validity?: AdminDriverFileValidityPayload,
): Promise<AdminDriverAssistDriver> {
  const formData = new FormData();
  formData.append('file', file);
  Object.entries(cleanParams(validity)).forEach(([key, value]) => {
    formData.append(key, String(value));
  });

  try {
    const response = await api.post(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/files/${encodeURIComponent(fileType)}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      },
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not upload the driver document.'));
  }
}

export async function updateAdminDriverOnboardingFileValidity(
  driverId: string,
  fileType: string,
  payload: AdminDriverFileValidityPayload,
): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.patch(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/files/${encodeURIComponent(fileType)}/validity`,
      cleanParams(payload),
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update the document validity dates.'));
  }
}

export async function completeAdminDriverOnboardingPersonalDocs(driverId: string): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.post(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/personal-docs/complete`,
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not mark personal documents complete.'));
  }
}

export async function updateAdminDriverOnboardingVehicleDetails(
  driverId: string,
  payload: AdminDriverVehicleDetailsPayload,
): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.put(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/vehicle-details`,
      payload,
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save the vehicle details.'));
  }
}

export async function completeAdminDriverOnboardingVehicleInfo(driverId: string): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.post(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/vehicle-info/complete`,
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not mark vehicle information complete.'));
  }
}

export async function updateAdminDriverOnboardingAccountType(
  driverId: string,
  type: 'INDIVIDUAL_OWNER' | 'COMPANY_DRIVER',
): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.put(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/account-type`,
      { type },
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save the driver account type.'));
  }
}

export async function updateAdminDriverOnboardingCompanyDetails(
  driverId: string,
  payload: AdminDriverCompanyDetailsPayload,
): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.put(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/company-details`,
      cleanParams(payload),
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save the company details.'));
  }
}

export async function updateAdminDriverOnboardingBankDetails(
  driverId: string,
  payload: AdminDriverBankDetailsPayload,
): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.put(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/bank-details`,
      cleanParams(payload),
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not save the bank details.'));
  }
}

export async function submitAdminDriverOnboardingForReview(driverId: string): Promise<AdminDriverAssistDriver> {
  try {
    const response = await api.post(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/submit-for-review`,
    );
    return mapAdminDriverAssistResponse(response.data, driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not submit the driver for review.'));
  }
}

export async function sendAdminDriverPasswordSetup(driverId: string): Promise<{ challengeId: string; message: string }> {
  try {
    const response = await api.post(
      `${ADMIN_DRIVER_ONBOARDING_ASSIST_BASE}/${encodeURIComponent(driverId)}/password-setup`,
    );
    return {
      challengeId: String(response.data?.challengeId ?? ''),
      message: response.data?.message ?? 'Password setup email sent.',
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not send the password setup email.'));
  }
}

export async function updateAdminDriverLockStatus(driverId: string, locked: boolean): Promise<AdminDriverReviewDetail['driver']> {
  try {
    const response = await api.put(`/driver-management/api/admin/drivers/${encodeURIComponent(driverId)}/lock`, {
      locked,
    });
    return mapAdminDriverReviewDriver(response.data ?? { id: driverId });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update the driver lock status.'));
  }
}

export async function updateAdminDriverEnabledStatus(driverId: string, enabled: boolean): Promise<AdminDriverReviewDetail['driver']> {
  try {
    const response = await api.put(`/driver-management/api/admin/drivers/${encodeURIComponent(driverId)}/enabled`, {
      enabled,
    });
    return mapAdminDriverReviewDriver(response.data ?? { id: driverId });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update the driver enabled status.'));
  }
}

export async function updateAdminDriverEarlyAdopterStatus(driverId: string, earlyAdopter: boolean): Promise<AdminDriverReviewDetail['driver']> {
  try {
    const response = await api.put(`/driver-management/api/admin/drivers/${encodeURIComponent(driverId)}/early-adopter`, {
      earlyAdopter,
    });
    return mapAdminDriverReviewDriver(response.data ?? { id: driverId });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update the driver early-adopter status.'));
  }
}

export async function updateAdminDriverPaystackSubaccount(
  driverId: string,
  payload: PaystackSubaccountUpdatePayload,
): Promise<AdminDriverReviewDetail['driver']> {
  try {
    const response = await api.put(
      `/driver-management/api/admin/drivers/${encodeURIComponent(driverId)}/paystack-subaccount`,
      cleanPaystackSubaccountPayload(payload),
    );
    return mapAdminDriverReviewDriver(response.data ?? { id: driverId });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update the driver Paystack subaccount.'));
  }
}

export async function updateAdminDriverGitReview(
  driverId: string,
  payload: AdminDriverGitReviewPayload,
): Promise<AdminDriverReviewDetail['driver']> {
  try {
    const response = await api.put(
      `/driver-management/api/admin/drivers/${encodeURIComponent(driverId)}/git-review`,
      payload,
    );
    return mapAdminDriverReviewDriver(response.data ?? { id: driverId });
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update the driver GIT review.'));
  }
}

export async function rerunAdminDriverCompanyMatching(driverId: string): Promise<AdminCompanyMatchingRefreshResponse> {
  try {
    const response = await api.post(`/driver-management/api/admin/company-matching/rerun/driver/${encodeURIComponent(driverId)}`);
    return mapCompanyMatchingRefreshResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not rerun company matching for this driver.'));
  }
}

export async function rerunAdminCompanyMatching(companyUuid: string): Promise<AdminCompanyMatchingRefreshResponse> {
  try {
    const response = await api.post(`/driver-management/api/admin/company-matching/rerun/company/${encodeURIComponent(companyUuid)}`);
    return mapCompanyMatchingRefreshResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not rerun company matching for this company.'));
  }
}

export async function approveAdminDriver(
  driverId: string,
  payload: { dangerousGoodsAccredited: boolean },
): Promise<AdminDriverReviewDetail> {
  try {
    await api.put(`/driver-management/api/admin/drivers/${encodeURIComponent(driverId)}/approve`, payload);
    return await fetchAdminDriverReview(driverId);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not approve the driver onboarding.'));
  }
}

export async function rejectAdminDriver(payload: {
  driverId: string;
  reason: string;
  messageSentSeparately: boolean;
}): Promise<AdminDriverReviewDetail> {
  try {
    const response = await api.put(
      `/driver-management/api/admin/drivers/${encodeURIComponent(payload.driverId)}/reject`,
      {
        reason: payload.reason,
        messageSentSeparately: payload.messageSentSeparately,
      },
    );
    return mapDriverReviewDetail(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not reject the driver onboarding.'));
  }
}

export async function approveAdminDriverChangeRequest(payload: {
  changeRequestId: string;
  reviewedBy?: string | null;
}): Promise<void> {
  try {
    await api.post(
      `/driver-management/api/admin/drivers/profile-changes/${encodeURIComponent(payload.changeRequestId)}/approve`,
      null,
      {
        params: payload.reviewedBy ? { reviewedBy: payload.reviewedBy } : undefined,
      },
    );
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not approve the driver profile change.'));
  }
}

export async function rejectAdminDriverChangeRequest(payload: {
  changeRequestId: string;
  reason: string;
  reviewedBy?: string | null;
}): Promise<void> {
  try {
    await api.post(
      `/driver-management/api/admin/drivers/profile-changes/${encodeURIComponent(payload.changeRequestId)}/reject`,
      {
        reason: payload.reason,
        ...(payload.reviewedBy ? { reviewedBy: payload.reviewedBy } : {}),
      },
    );
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not reject the driver profile change.'));
  }
}

export async function fetchAdminDriverEarnings(driverId: string): Promise<AdminDriverEarningRecord[]> {
  try {
    const response = await api.get(`/driver-management/api/admin/drivers/${encodeURIComponent(driverId)}/earnings`);
    return Array.isArray(response.data)
      ? response.data.map((item: any) => ({
          bookingId: item.bookingId ?? null,
          tripId: item.tripId ?? null,
          driverId: item.driverId ?? null,
          completedAt: item.completedAt ?? null,
          grossAmount: item.grossAmount ?? 0,
          commissionAmount: item.commissionAmount ?? 0,
          netAmount: item.netAmount ?? 0,
          currency: item.currency ?? null,
          commissionPlanCode: item.commissionPlanCode ?? null,
          commissionRateBps: item.commissionRateBps ?? null,
          earlyAdopter: item.earlyAdopter ?? null,
        }))
      : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load driver earnings.'));
  }
}

export async function fetchAdminCommissionSummary(params?: Record<string, unknown>): Promise<AdminCommissionSummary> {
  try {
    const response = await api.get('/driver-management/api/admin/commission-summary', { params });
    return mapCommissionSummary(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the commission summary.'));
  }
}

export async function fetchAdminPaymentIntents(
  params: AdminPaymentIntentSearchParams = {},
): Promise<AdminPaymentIntent[]> {
  try {
    const response = await api.get(`${ADMIN_PAYMENT_OPS_BASE}/payment-intents`, {
      params: cleanParams(params),
    });
    return Array.isArray(response.data) ? response.data.map(mapAdminPaymentIntent) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load payment intents.'));
  }
}

export async function fetchAdminPaymentIntentDetail(publicId: string): Promise<AdminPaymentIntentDetail> {
  try {
    const response = await api.get(
      `${ADMIN_PAYMENT_OPS_BASE}/payment-intents/${encodeURIComponent(publicId)}`,
    );
    return mapAdminPaymentIntentDetail(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load payment intent details.'));
  }
}

export async function declareAdminManualCollection(
  publicId: string,
  payload: AdminManualCollectionPayload,
): Promise<AdminPaymentIntent> {
  try {
    const response = await api.post(
      `${ADMIN_PAYMENT_OPS_BASE}/payment-intents/${encodeURIComponent(publicId)}/manual-collections`,
      mapAdminManualCollectionPayload(payload),
    );
    return mapAdminPaymentIntent(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not declare the offline collection.'));
  }
}

export async function fetchAdminManualCollections(
  params: AdminManualCollectionSearchParams = {},
): Promise<AdminManualCollection[]> {
  try {
    const response = await api.get(`${ADMIN_PAYMENT_OPS_BASE}/manual-collections`, {
      params: cleanParams(params),
    });
    return Array.isArray(response.data) ? response.data.map(mapAdminManualCollection) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load manual collections.'));
  }
}

export async function voidAdminManualCollection(
  publicId: string,
  payload: AdminPaymentActionPayload,
): Promise<AdminPaymentIntent> {
  try {
    const response = await api.post(
      `${ADMIN_PAYMENT_OPS_BASE}/manual-collections/${encodeURIComponent(publicId)}/void`,
      mapAdminPaymentActionPayload(payload),
    );
    return mapAdminPaymentIntent(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not void the manual collection.'));
  }
}

export async function fetchAdminPayouts(params: AdminPayoutSearchParams = {}): Promise<AdminPayout[]> {
  try {
    const response = await api.get(`${ADMIN_PAYMENT_OPS_BASE}/payouts`, {
      params: cleanParams(params),
    });
    return Array.isArray(response.data) ? response.data.map(mapAdminPayout) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load payouts.'));
  }
}

export async function fetchAdminProviderEvents(
  params: AdminProviderEventSearchParams = {},
): Promise<AdminProviderEvent[]> {
  try {
    const response = await api.get(`${ADMIN_PAYMENT_OPS_BASE}/provider-events`, {
      params: cleanParams(params),
    });
    return Array.isArray(response.data) ? response.data.map(mapAdminProviderEvent) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load provider events.'));
  }
}

export async function fetchAdminWebhookEvents(
  params: AdminWebhookEventSearchParams = {},
): Promise<AdminWebhookEvent[]> {
  try {
    const response = await api.get(`${ADMIN_PAYMENT_OPS_BASE}/webhook-events`, {
      params: cleanParams(params),
    });
    return Array.isArray(response.data) ? response.data.map(mapAdminWebhookEvent) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load webhook events.'));
  }
}

export async function refundAdminPaymentIntent(
  publicId: string,
  payload: AdminPaymentActionPayload,
): Promise<AdminPaymentIntent> {
  try {
    const response = await api.post(
      `${ADMIN_PAYMENT_OPS_BASE}/payment-intents/${encodeURIComponent(publicId)}/refund`,
      mapAdminPaymentActionPayload(payload),
    );
    return mapAdminPaymentIntent(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not refund the payment intent.'));
  }
}

export async function holdAdminPayout(
  publicId: string,
  payload: AdminPaymentActionPayload,
): Promise<AdminPayout> {
  try {
    const response = await api.post(
      `${ADMIN_PAYMENT_OPS_BASE}/payouts/${encodeURIComponent(publicId)}/hold`,
      mapAdminPaymentActionPayload(payload),
    );
    return mapAdminPayout(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not hold the payout.'));
  }
}

export async function releaseAdminPayout(
  publicId: string,
  payload: AdminPaymentActionPayload,
): Promise<AdminPayout> {
  try {
    const response = await api.post(
      `${ADMIN_PAYMENT_OPS_BASE}/payouts/${encodeURIComponent(publicId)}/release`,
      mapAdminPaymentActionPayload(payload),
    );
    return mapAdminPayout(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not release the payout.'));
  }
}

export async function retryAdminPayout(
  publicId: string,
  payload: AdminPaymentActionPayload,
): Promise<AdminPayoutBatchRun> {
  try {
    const response = await api.post(
      `${ADMIN_PAYMENT_OPS_BASE}/payouts/${encodeURIComponent(publicId)}/retry`,
      mapAdminPaymentActionPayload(payload),
    );
    return mapAdminPayoutBatchRun(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not retry the payout.'));
  }
}

export async function replayAdminWebhook(
  publicId: string,
  payload: AdminPaymentActionPayload,
): Promise<AdminWebhookEvent> {
  try {
    const response = await api.post(
      `${ADMIN_PAYMENT_OPS_BASE}/webhook-events/${encodeURIComponent(publicId)}/replay`,
      mapAdminPaymentActionPayload(payload),
    );
    return mapAdminWebhookEvent(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not replay the webhook.'));
  }
}

export async function fetchAdminCompanyPayoutReviews(params: Record<string, unknown>): Promise<{
  items: AdminCompanyPayoutReview[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/driver-management/api/admin/company-payout-details', { params });
    return mapPageResponse(response.data, mapCompanyPayoutReview);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load payout reviews.'));
  }
}

export async function fetchAdminCompanyPayoutReviewDetail(companyUuid: string): Promise<AdminCompanyPayoutReview> {
  try {
    const response = await api.get(`/driver-management/api/admin/company-payout-details/${encodeURIComponent(companyUuid)}`);
    return mapCompanyPayoutReview(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load payout review details.'));
  }
}

export async function updateAdminCompanyPayoutPaystackSubaccount(
  companyUuid: string,
  payload: PaystackSubaccountUpdatePayload,
): Promise<AdminCompanyPayoutReview> {
  try {
    const response = await api.put(
      `/driver-management/api/admin/company-payout-details/${encodeURIComponent(companyUuid)}/paystack-subaccount`,
      cleanPaystackSubaccountPayload(payload),
    );
    return mapCompanyPayoutReview(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update the company Paystack subaccount.'));
  }
}

export async function updateAdminCompanyPayoutStatus(payload: {
  companyUuid: string;
  actorUserId: string;
  status: string;
  reason?: string;
}): Promise<AdminCompanyPayoutReview> {
  try {
    const response = await api.put(
      `/driver-management/api/admin/company-payout-details/${encodeURIComponent(payload.companyUuid)}/status`,
      {
        status: payload.status,
        ...(payload.reason?.trim() ? { reason: payload.reason.trim() } : {}),
      },
      {
        params: { actorUserId: payload.actorUserId },
      },
    );
    return mapCompanyPayoutReview(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update payout review status.'));
  }
}

export async function fetchAdminReviewConversation(driverId: string): Promise<AdminReviewConversation> {
  try {
    const response = await api.get(`/messaging-service/api/admin-review/drivers/${encodeURIComponent(driverId)}/conversation`);
    return mapConversation(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the admin review conversation.'));
  }
}

export async function fetchAdminConversationMessages(threadKey: string): Promise<AdminReviewMessage[]> {
  try {
    const response = await api.get(
      `/messaging-service/api/messaging/conversations/${encodeURIComponent(threadKey)}/messages`,
      { params: { page: 0, size: 50 } },
    );
    const content = Array.isArray(response.data?.content) ? response.data.content : [];
    return content.map(mapMessage).reverse();
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load review messages.'));
  }
}

export async function sendAdminReviewMessage(payload: {
  driverId: string;
  content: string;
}): Promise<AdminReviewMessage> {
  try {
    const response = await api.post(
      `/messaging-service/api/admin-review/drivers/${encodeURIComponent(payload.driverId)}/messages`,
      { content: payload.content },
    );
    return mapMessage(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not send the admin review message.'));
  }
}

export async function fetchAdminUserReviewConversation(userId: string): Promise<AdminReviewConversation> {
  try {
    const response = await api.get(`/messaging-service/api/admin-review/users/${encodeURIComponent(userId)}/conversation`);
    return mapConversation(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the user review conversation.'));
  }
}

export async function sendAdminUserReviewMessage(payload: {
  userId: string;
  content: string;
}): Promise<AdminReviewMessage> {
  try {
    const response = await api.post(
      `/messaging-service/api/admin-review/users/${encodeURIComponent(payload.userId)}/messages`,
      { content: payload.content },
    );
    return mapMessage(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not send the user review message.'));
  }
}

export async function fetchAdminSupportInbox(params: {
  page?: number;
  size?: number;
} = {}): Promise<AdminSupportInboxPage> {
  try {
    const response = await api.get('/messaging-service/api/support/admin/inbox', {
      params: {
        page: params.page ?? 0,
        size: params.size ?? 50,
      },
    });
    return {
      content: Array.isArray(response.data?.content) ? response.data.content : [],
      page: Number(response.data?.page ?? params.page ?? 0),
      size: Number(response.data?.size ?? params.size ?? 50),
      totalElements: Number(response.data?.totalElements ?? 0),
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load support inbox.'));
  }
}

export async function fetchAdminSupportMessages(supportKey: string): Promise<AdminReviewMessage[]> {
  try {
    const response = await api.get(
      `/messaging-service/api/support/admin/conversations/${encodeURIComponent(supportKey)}/messages`,
      { params: { page: 0, size: 50 } },
    );
    const content = Array.isArray(response.data?.content) ? response.data.content : [];
    return content.map(mapMessage).reverse();
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load support messages.'));
  }
}

export async function sendAdminSupportMessage(payload: {
  supportKey: string;
  content: string;
}): Promise<AdminReviewMessage> {
  try {
    const response = await api.post(
      `/messaging-service/api/support/admin/conversations/${encodeURIComponent(payload.supportKey)}/messages`,
      { content: payload.content },
    );
    return mapMessage(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not send the support message.'));
  }
}

export async function fetchAdminUsers(): Promise<AdminUser[]> {
  try {
    const response = await api.get('/user-management/api/admin/getusers');
    return Array.isArray(response.data) ? response.data.map(mapAdminUser) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load admin users.'));
  }
}

export async function fetchAdminUsersPage(params: Record<string, unknown>): Promise<{
  items: AdminUser[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/user-management/api/admin/users', { params });
    return mapPageResponse(response.data, mapAdminUser);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load admin users.'));
  }
}

export async function fetchAdminBusinessProfiles(params: Record<string, unknown>): Promise<{
  items: AdminBusinessProfile[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/user-management/api/business-onboarding/admin/profiles', { params });
    return mapPageResponse(response.data, mapAdminBusinessProfile);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load business profiles.'));
  }
}

export async function fetchAdminBusinessProfile(businessProfileId: string): Promise<AdminBusinessProfile> {
  try {
    const response = await api.get(
      `/user-management/api/business-onboarding/admin/profiles/${encodeURIComponent(businessProfileId)}`,
    );
    return mapAdminBusinessProfile(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load business profile details.'));
  }
}

export async function reviewAdminBusinessProfile(
  businessProfileId: string,
  payload: { approved: boolean; note?: string },
): Promise<AdminBusinessProfile> {
  try {
    const response = await api.post(
      `/user-management/api/business-onboarding/${encodeURIComponent(businessProfileId)}/review`,
      payload,
    );
    return mapAdminBusinessProfile(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not review business profile.'));
  }
}

export async function fetchAdminTripsSummary(): Promise<AdminTripsSummary> {
  try {
    const response = await api.get('/booking-management/api/admin/trips/summary');
    return mapTripsSummary(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the trips summary.'));
  }
}

export async function fetchAdminEngagementDashboard(
  params: AdminEngagementDashboardParams = {},
): Promise<AdminEngagementDashboard> {
  try {
    const response = await api.get('/booking-management/api/admin/engagement-dashboard/summary', {
      params: cleanParams(params),
    });
    return mapAdminEngagementDashboard(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the engagement dashboard.'));
  }
}

export async function fetchAdminDeclaredLoadDashboard(
  params: AdminDeclaredLoadDashboardParams = {},
): Promise<AdminDeclaredLoadDashboard> {
  try {
    const response = await api.get('/booking-management/api/admin/trips/declared-loads/dashboard', {
      params: cleanParams(params),
    });
    return mapAdminDeclaredLoadDashboard(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the declared-load dashboard.'));
  }
}

export async function fetchAdminDeclaredLoads(params: AdminDeclaredLoadListParams = {}): Promise<{
  items: AdminDeclaredLoadListItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/booking-management/api/admin/trips/declared-loads', {
      params: cleanParams(params),
    });
    return mapPageResponse(response.data, mapAdminDeclaredLoadListItem);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load declared loads.'));
  }
}

export async function fetchActiveAdminCustomerPromotion(): Promise<AdminCustomerPromotion> {
  try {
    const response = await api.get('/booking-management/api/admin/customer-promotions/active');
    return mapAdminCustomerPromotion(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the active customer promotion.'));
  }
}

export async function updateActiveAdminCustomerPromotion(
  payload: AdminCustomerPromotionPayload,
): Promise<AdminCustomerPromotion> {
  try {
    const response = await api.put(
      '/booking-management/api/admin/customer-promotions/active',
      cleanParams(payload),
    );
    return mapAdminCustomerPromotion(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update the customer promotion.'));
  }
}

export async function fetchAdminCustomerPromotionHistory(params: Record<string, unknown> = {}): Promise<{
  items: AdminCustomerPromotion[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/booking-management/api/admin/customer-promotions/history', {
      params: cleanParams(params),
    });
    return mapPageResponse(response.data, mapAdminCustomerPromotion);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load customer promotion history.'));
  }
}

export async function fetchAdminDriverCampaigns(params: Record<string, unknown> = {}): Promise<{
  items: AdminDriverCampaign[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/booking-management/api/admin/driver-campaigns', {
      params: cleanParams(params),
    });
    return mapPageResponse(response.data, mapAdminDriverCampaign);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load driver campaigns.'));
  }
}

export async function createAdminDriverCampaign(
  payload: AdminDriverCampaignPayload,
): Promise<AdminDriverCampaign> {
  try {
    const response = await api.post('/booking-management/api/admin/driver-campaigns', cleanParams(payload));
    return mapAdminDriverCampaign(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not create the driver campaign.'));
  }
}

export async function updateAdminDriverCampaign(
  campaignId: string,
  payload: AdminDriverCampaignPayload,
): Promise<AdminDriverCampaign> {
  try {
    const response = await api.put(
      `/booking-management/api/admin/driver-campaigns/${encodeURIComponent(campaignId)}`,
      cleanParams(payload),
    );
    return mapAdminDriverCampaign(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update the driver campaign.'));
  }
}

export async function updateAdminDriverCampaignStatus(
  campaignId: string,
  payload: { status: string; reason?: string; idempotencyKey?: string },
): Promise<AdminDriverCampaign> {
  try {
    const response = await api.put(
      `/booking-management/api/admin/driver-campaigns/${encodeURIComponent(campaignId)}/status`,
      cleanParams(payload),
    );
    return mapAdminDriverCampaign(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not update the driver campaign status.'));
  }
}

export async function fetchAdminDriverCampaignEnrollments(
  campaignId: string,
  params: Record<string, unknown> = {},
): Promise<{
  items: AdminDriverCampaignEnrollment[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get(
      `/booking-management/api/admin/driver-campaigns/${encodeURIComponent(campaignId)}/enrollments`,
      { params: cleanParams(params) },
    );
    return mapPageResponse(response.data, mapAdminDriverCampaignEnrollment);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load campaign enrollments.'));
  }
}

export async function autoEnrollAdminDriverCampaign(
  payload: AdminDriverCampaignEnrollmentPayload,
): Promise<AdminDriverCampaignEnrollmentBatch> {
  try {
    const response = await api.post('/booking-management/api/admin/driver-campaigns/auto-enroll', cleanParams(payload));
    return mapAdminDriverCampaignEnrollmentBatch(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not enroll drivers in this campaign.'));
  }
}

export async function removeAdminDriverCampaignEnrollment(
  campaignId: string,
  driverId: string,
  payload: AdminDriverCampaignEnrollmentRemovalPayload = {},
): Promise<AdminDriverCampaignEnrollment> {
  try {
    const response = await api.post(
      `/booking-management/api/admin/driver-campaigns/${encodeURIComponent(campaignId)}/drivers/${encodeURIComponent(driverId)}/remove`,
      cleanParams(payload),
    );
    return mapAdminDriverCampaignEnrollment(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not remove this driver from the campaign.'));
  }
}

export async function fetchAdminDriverCampaignSummary(
  campaignId: string,
  driverId: string,
): Promise<AdminDriverCampaignSummary> {
  try {
    const response = await api.get(
      `/booking-management/api/admin/driver-campaigns/${encodeURIComponent(campaignId)}/drivers/${encodeURIComponent(driverId)}/summary`,
    );
    return mapAdminDriverCampaignSummary(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the driver campaign summary.'));
  }
}

export async function fetchAdminDriverCampaignDeliveries(
  campaignId: string,
  driverId: string,
  params: Record<string, unknown> = {},
): Promise<{
  items: AdminDriverCampaignDelivery[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get(
      `/booking-management/api/admin/driver-campaigns/${encodeURIComponent(campaignId)}/drivers/${encodeURIComponent(driverId)}/deliveries`,
      { params: cleanParams(params) },
    );
    return mapPageResponse(response.data, mapAdminDriverCampaignDelivery);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the driver campaign deliveries.'));
  }
}

export async function fetchAdminDriverCampaignLedger(
  campaignId: string,
  params: Record<string, unknown> = {},
): Promise<{
  items: AdminDriverCampaignLedgerTransaction[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get(
      `/booking-management/api/admin/driver-campaigns/${encodeURIComponent(campaignId)}/ledger`,
      { params: cleanParams(params) },
    );
    return mapPageResponse(response.data, mapAdminDriverCampaignLedgerTransaction);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the driver campaign ledger.'));
  }
}

export async function markAdminDriverCampaignPaid(
  campaignId: string,
  driverId: string,
  payload: AdminDriverCampaignPaymentPayload,
): Promise<AdminDriverCampaignLedgerTransaction> {
  try {
    const response = await api.post(
      `/booking-management/api/admin/driver-campaigns/${encodeURIComponent(campaignId)}/drivers/${encodeURIComponent(driverId)}/payments`,
      cleanParams(payload),
    );
    return mapAdminDriverCampaignLedgerTransaction(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not mark this campaign payout as paid.'));
  }
}

export async function previewAdminEmailCampaign(
  payload: AdminEmailCampaignPayload,
): Promise<AdminEmailCampaignPreview> {
  try {
    const response = await api.post(`${ADMIN_COMMUNICATIONS_BASE}/preview`, payload);
    return mapAdminEmailCampaignPreview(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not preview the email campaign.'));
  }
}

export async function sendAdminEmailCampaign(payload: AdminEmailCampaignPayload): Promise<AdminEmailCampaign> {
  try {
    const response = await api.post(`${ADMIN_COMMUNICATIONS_BASE}/send`, payload);
    return mapAdminEmailCampaign(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not queue the email campaign.'));
  }
}

export async function fetchAdminEmailCampaigns(params: {
  page?: number;
  size?: number;
} = {}): Promise<AdminEmailCampaign[]> {
  try {
    const response = await api.get(ADMIN_COMMUNICATIONS_BASE, {
      params: {
        page: params.page ?? 0,
        size: params.size ?? 20,
      },
    });
    return Array.isArray(response.data) ? response.data.map(mapAdminEmailCampaign) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load email campaigns.'));
  }
}

export async function fetchAdminEmailCampaignDetail(campaignId: string): Promise<AdminEmailCampaignDetail> {
  try {
    const response = await api.get(`${ADMIN_COMMUNICATIONS_BASE}/${encodeURIComponent(campaignId)}`);
    return mapAdminEmailCampaignDetail(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load email campaign details.'));
  }
}

export async function retryAdminEmailCampaign(campaignId: string): Promise<AdminEmailCampaign> {
  try {
    const response = await api.post(`${ADMIN_COMMUNICATIONS_BASE}/${encodeURIComponent(campaignId)}/retry`);
    return mapAdminEmailCampaign(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not retry this email campaign.'));
  }
}

export async function fetchAdminTrips(params: Record<string, unknown>): Promise<{
  items: AdminTripListItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/booking-management/api/admin/trips', { params });
    return mapPageResponse(response.data, mapTripListItem);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load admin trips.'));
  }
}

export async function fetchAdminAssistedDispatchQueue(params: Record<string, unknown> = {}): Promise<{
  items: AdminAssistedDispatchQueueItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/booking-management/api/admin/trips/assisted-dispatch/queue', {
      params: cleanParams(params),
    });
    return mapPageResponse(response.data, mapAssistedDispatchQueueItem);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load assisted dispatch bookings.'));
  }
}

export async function fetchAdminAssistedDispatchCandidates(
  tripId: string,
  params: Record<string, unknown> = {},
): Promise<AdminAssistedDispatchCandidate[]> {
  try {
    const response = await api.get(
      `/booking-management/api/admin/trips/${encodeURIComponent(tripId)}/assisted-dispatch/candidates`,
      { params: cleanParams(params) },
    );
    return Array.isArray(response.data) ? response.data.map(mapAssistedDispatchCandidate) : [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not find drivers near pickup.'));
  }
}

export async function assignAdminAssistedDispatchDriver(
  tripId: string,
  payload: { bookingId: string; driverId: string; reason: string },
): Promise<AdminAssistedDispatchAssignResponse> {
  try {
    const response = await api.post(
      `/booking-management/api/admin/trips/${encodeURIComponent(tripId)}/assisted-dispatch/assign`,
      {
        booking_id: payload.bookingId,
        driver_id: payload.driverId,
        reason: payload.reason,
      },
    );
    return mapAssistedDispatchAssignResponse(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not assign the selected driver.'));
  }
}

export async function sendAdminWhatsAppMessage(payload: {
  to: string;
  body: string;
}): Promise<AdminWhatsAppMessageResponse> {
  try {
    const response = await api.post('/booking-management/api/admin/trips/whatsapp/messages', payload);
    return { externalMessageId: String(response.data?.externalMessageId ?? '') };
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not send the WhatsApp message.'));
  }
}

export async function searchAdminSupportTrips(params: Record<string, unknown>): Promise<{
  items: AdminTripListItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}> {
  try {
    const response = await api.get('/booking-management/api/admin/trips/support-search', { params });
    return mapPageResponse(response.data, mapTripListItem);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not search support trips.'));
  }
}

export async function resolveAdminSupportTripClose(
  payload: AdminTripResolutionPayload,
): Promise<AdminTripResolutionResponse> {
  try {
    const response = await api.post('/booking-management/api/admin/trips/support/resolve-close', payload);
    return mapTripResolution(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not close the trip through support.'));
  }
}

export async function fetchAdminTripDetail(tripId: string): Promise<AdminTripDetail> {
  try {
    const response = await api.get(`/booking-management/api/admin/trips/${encodeURIComponent(tripId)}`);
    return mapTripDetail(response.data);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Could not load the trip detail.'));
  }
}
